import { useCallback, useRef, useState } from 'react';
import moment from 'moment';
import 'moment-timezone';
import { timestampToDate } from './dates';
import {
  BookingStatus,
  crewRules,
  DEPOSIT_CHECK_KEY,
  DEPOSIT_INSTRUCTIONS_KEY,
  DURATION_TYPE,
  FILTERED_TRANSITIONS_SET,
  NEXT_STATE_ICON_MAP,
  PAYMENT_METHODS,
  transactionStatusIntlMap,
  TRANSITIONS,
} from './enums';
import { getBookingStatusStyle } from './bookingStatusAppearance';
import { detectPaymentCard } from './cardDetection';
import * as validators from './validators';
import { types as sdkTypes } from '../util/sdkLoader';
import { transitions } from '../transactions/transactionProcessBooking';
import {
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_ITEM,
  LINE_ITEM_NIGHT,
  LINE_ITEM_OMISE_FEE,
  LINE_ITEM_PROVIDER_COMMISSION,
} from './types';
import { resolveLatestProcessName } from '../transactions/transaction';
import { getSubMerchantIdApi } from './api';
import { FIELD_HEADING_TH, GROUP_HEADING_TH, OPTION_LABEL_TH } from './thaiFields';
import { getUserType as getCanonicalUserType, isHostUser } from './bookingRestriction';

import redFlag from '../assets/redFlag.png';
import yellowFlag from '../assets/yellowFlag.png';
import orangeFlag from '../assets/orangeFlag.png';
import { useIntl } from 'react-intl';

const { Money, UUID } = sdkTypes;

const OMISE_SRC = 'https://cdn.omise.co/omise.js';
const OMISE_SCRIPT_ATTR = 'data-omise';
let omiseLoaderPromise; // shared across renders/components

const LINE_ITEM_CUSTOM_OFFER = 'line-item/custom-offer';
const LINE_ITEM_FIXED = 'line-item/fixed';
const BOOKING_BASE_LINE_ITEM_CODES = [
  LINE_ITEM_CUSTOM_OFFER,
  LINE_ITEM_HOUR,
  LINE_ITEM_DAY,
  LINE_ITEM_NIGHT,
  LINE_ITEM_ITEM,
  LINE_ITEM_FIXED,
];

export const getBookingBaseLineItem = lineItems =>
  Array.isArray(lineItems)
    ? lineItems.find(item => BOOKING_BASE_LINE_ITEM_CODES.includes(item.code) && !item.reversal)
    : null;

export const getMoneySubunits = money => {
  const amount = Number(money?.amount);
  return Number.isFinite(amount) ? amount : 0;
};

export const isArrayLength = arr => {
  return Array.isArray(arr) && (arr.length > 0 ?? false);
};

export const getTitleFromError = error => {
  if (error && error.apiErrors && Array.isArray(error.apiErrors) && error.apiErrors.length > 0) {
    const firstError = error.apiErrors[0];
    return firstError.title;
  }
  return null;
};

export const extractRescheduleBookingDetails = transaction => {
  const rescheduleBookingParams = transaction?.attributes?.metadata?.rescheduleBookingParams || [];

  if (rescheduleBookingParams.length === 0) return null;

  // ✅ Pick the latest by createdDate
  const latestDetail = rescheduleBookingParams.reduce((latest, current) => {
    const latestDate = moment(latest.createdDate, 'YYYY-MM-DD HH:mm:ss');
    const currentDate = moment(current.createdDate, 'YYYY-MM-DD HH:mm:ss');
    return currentDate.isAfter(latestDate) ? current : latest;
  }, rescheduleBookingParams[0]);

  const startUnix = latestDetail.rescheduleStartUnix;
  const endUnix = latestDetail.rescheduleEndUnix;

  return {
    start: startUnix ? moment.unix(startUnix / 1000).format('MMM D') : null,
    end: endUnix ? moment.unix(endUnix / 1000).format('MMM D') : null,
    createdDate: latestDetail.createdDate,
    status: latestDetail.status,
    formattedStartDate: startUnix && timestampToDate(startUnix),
    formattedEndDate: endUnix && timestampToDate(endUnix),
  };
};

const RESCHEDULE_BOOKING_REQUEST_STATUS = 'requested';

const RESCHEDULE_REQUEST_TRANSITIONS = new Set([
  TRANSITIONS.RESCHEDULE_BOOKING,
  TRANSITIONS.TRANSITION_RESCHEDULE_BOOKING,
]);

const RESCHEDULE_SETTLED_TRANSITIONS = new Set([
  TRANSITIONS.ACCEPT_RESCHEDULE_BOOKING,
  TRANSITIONS.DECLINE_RESCHEDULE_BOOKING,
]);

const getTransitionName = transition =>
  typeof transition === 'string' ? transition : transition?.transition || transition?.name || '';

const getTransitionHistoryWithLastTransition = transaction => {
  const transitionsArr = Array.isArray(transaction?.attributes?.transitions)
    ? transaction.attributes.transitions
    : [];
  const lastTransition = transaction?.attributes?.lastTransition;
  const lastHistoryTransition = getTransitionName(transitionsArr[transitionsArr.length - 1]);

  return lastTransition && lastHistoryTransition !== lastTransition
    ? [...transitionsArr, { transition: lastTransition }]
    : transitionsArr;
};

export const hasPendingRescheduleRequest = transaction => {
  if (transaction?.attributes?.protectedData?.isCustomOfferBooking) {
    return false;
  }

  const latestRescheduleDetails = extractRescheduleBookingDetails(transaction);
  const latestMetadataStatus = String(latestRescheduleDetails?.status || '').toLowerCase();

  if (latestMetadataStatus && latestMetadataStatus !== RESCHEDULE_BOOKING_REQUEST_STATUS) {
    return false;
  }

  const latestRescheduleTransition = getTransitionHistoryWithLastTransition(transaction)
    .map(getTransitionName)
    .filter(
      transition =>
        RESCHEDULE_REQUEST_TRANSITIONS.has(transition) ||
        RESCHEDULE_SETTLED_TRANSITIONS.has(transition)
    )
    .pop();

  if (latestRescheduleTransition) {
    return RESCHEDULE_REQUEST_TRANSITIONS.has(latestRescheduleTransition);
  }

  return latestMetadataStatus === RESCHEDULE_BOOKING_REQUEST_STATUS;
};

export const getRescheduleBookingDetails = transaction => {
  const rescheduleBookingParams = transaction?.attributes?.metadata?.rescheduleBookingParams || [];
  const transitions = transaction?.attributes?.transitions || [];
  // Filter out the "reschedule-booking" transitions
  const rescheduleBookingTransitions = transitions.filter(
    transition => transition.transition === 'transition/reschedule-booking'
  );

  // Create an array of objects containing formattedStartDate, formattedEndDate, and paymentIntent for each "reschedule-booking"
  const result = rescheduleBookingTransitions.map((transition, index) => {
    const rescheduleBookingDetails = rescheduleBookingParams[index] || {};
    const rescheduleStartUnix = timestampToDate(rescheduleBookingDetails.rescheduleStartUnix);
    const rescheduleEndUnix = timestampToDate(rescheduleBookingDetails.rescheduleEndUnix);
    const status = rescheduleBookingDetails?.status;
    let lastEndDate = null;
    // If there is a lastEndDate, use it for the new start date; otherwise, use the current rescheduleStartUnix
    const start = rescheduleStartUnix ? moment(rescheduleStartUnix).format('MMM D, h:mm a') : null;
    const end = rescheduleEndUnix ? moment(rescheduleEndUnix).format('MMM D, h:mm a') : null;

    // Update lastEndDate to the current rescheduleEndUnix only if the status is 'accepted' or 'requested'
    if (status === 'accepted' || status === 'requested') {
      lastEndDate = rescheduleEndUnix ? moment(rescheduleEndUnix) : null;
    }

    // Set newStartDate only if the status is 'accepted' or 'requested'
    const newStartDate =
      (status === 'accepted' || status === 'requested') && end
        ? moment(rescheduleEndUnix).format('MMM D, h:mm A')
        : null;
    const createdDate = rescheduleBookingDetails.createdDate;

    return {
      start,
      end,
      transitionDetails: transition,
      createdDate,
      status,
      newStartDate,
    };
  });

  return result;
};

export const formatDateRangeForTimezone = dates => {
  if (!dates || (!dates.startDate && !dates.endDate)) return null;

  const { startDate, endDate } = dates;

  // Get the user's current timezone dynamically
  const timeZone = moment.tz.guess();

  // Function to format a date in the specified timezone
  const formatDate = date => {
    if (!date) return null;
    // Convert the date to the user's timezone and format it
    const zonedDate = moment(date).tz(timeZone);
    return zonedDate.format('YYYY-MM-DD');
  };

  const dateRange = [startDate ? formatDate(startDate) : null, endDate ? formatDate(endDate) : null]
    .filter(Boolean)
    .join(',');

  return dateRange || null;
};

export const getUserType = user => {
  return getCanonicalUserType(user);
};

export { isHostUser };

export const getInitialImage = listing => {
  if (!listing || !listing?.id) return null;

  return listing?.images?.[0]?.attributes?.variants?.['default']?.url || '';
};

export function getSquareSmallImageUrl(user) {
  try {
    return user?.profileImage?.attributes?.variants?.['default']?.url || null;
  } catch (error) {
    console.error('Error extracting square-small image URL:', error);
    return null;
  }
}

export function mergeAllTimeSlots(monthlyTimeSlots) {
  let mergedTimeSlots = [];

  // Loop through each month in the monthlyTimeSlots object
  for (const month in monthlyTimeSlots) {
    // Check if the month has timeSlots and it's an array
    if (Array.isArray(monthlyTimeSlots[month].timeSlots)) {
      // Concatenate the timeSlots array into the mergedTimeSlots array
      mergedTimeSlots = mergedTimeSlots.concat(monthlyTimeSlots[month].timeSlots);
    }
  }

  return mergedTimeSlots;
}

export function checkAvailableSlots(start, endDate, timeSlotsOnSelectedMonth = []) {
  // If either start or end date is not provided, return "no dates"
  if (!start || !endDate) {
    return 'no dates';
  }

  // Parse start and end dates to moment objects (full days)
  const startMoment = moment(start).add(1, 'day');
  const endMoment = moment(endDate).endOf('day'); // Ensure full day coverage for end date

  // Split the provided date range into separate ranges
  const result = splitDateRanges(startMoment, endMoment);
  // Ensure both start and end of every range in result are strictly within an available slot
  const isAvailable = result.every(range => {
    const rangeStart = moment(range.start).startOf('day');
    const rangeEnd = moment(range.end).endOf('day');

    // Check if the rangeStart and rangeEnd fall strictly within one of the slots
    const matchingSlot = timeSlotsOnSelectedMonth.some(slot => {
      const slotStart = moment(slot.attributes.start).startOf('day');
      const slotEnd = moment(slot.attributes.end).endOf('day');

      // The selected range must start after or at the start of the slot
      const isStartWithinSlot = rangeStart.isSameOrAfter(slotStart);
      // The selected range must end strictly before the end of the slot
      const isEndWithinSlot = rangeEnd.isSameOrBefore(slotEnd);

      // Ensure both conditions are true
      return isStartWithinSlot && isEndWithinSlot;
    });

    // If there's no matching slot that covers the entire range, return false
    if (!matchingSlot) {
      return false;
    }

    return true; // Slot fully covers the range
  });

  return isAvailable ? true : false;
}

export const convertPriceToFixedPrice = price => {
  if (isNaN(price) || price === null || price === undefined) {
    return '$0.00';
  }

  const formattedPrice = (price / 100).toFixed(2);
  return `$${formattedPrice}`;
};

export function getFilteredLineItems(transaction) {
  const { lineItems } = transaction.attributes;
  const filteredItems = lineItems.filter(
    item =>
      item.code !== 'line-item/night' &&
      item.code !== 'line-item/security-deposit' &&
      item.code !== 'line-item/provider-commission' &&
      item.code !== 'line-item/customer-commission'
  );

  return filteredItems.map(item => {
    return {
      code: item.code.replace('line-item/', ''),
      amount: item.unitPrice.amount,
    };
  });
}
/**
 * Returns a capitalized string.
 * @param {string} str - The string to capitalize.
 * @returns {string|null} The capitalized string, or null if input is invalid.
 */
export const capitalizeFirstLetter = str => {
  if (typeof str !== 'string' || str.length === 0) {
    return null;
  }

  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const validateDuration = (
  startTimestamp,
  endTimestamp,
  originalDurationInHours,
  setDurationError,
  intl
) => {
  if (!startTimestamp || !endTimestamp) {
    setDurationError(null);
    return;
  }
  const start = moment(Number(startTimestamp));
  const end = moment(Number(endTimestamp));

  const newDuration = end.diff(start, 'hours', true); // true = decimal precision
  const errorMsg = intl.formatMessage({ id: 'Reschedule.timeError' }, { originalDurationInHours });

  if (Math.round(newDuration) !== originalDurationInHours) {
    setDurationError(errorMsg);
  } else {
    setDurationError(null);
  }
};
export const getFullEndDateTime = dateTimeString => {
  if (!dateTimeString) return '';

  const [startDateTime = '', endTime = ''] = dateTimeString.split(' - ') || [];
  const datePart = startDateTime.split(',')[0];

  return datePart && endTime ? `${datePart}, ${endTime}` : endTime;
};

export const getOriginalDurationInHours = transaction => {
  if (!transaction?.booking?.attributes?.start || !transaction?.booking?.attributes?.end) {
    return null;
  }

  const start = moment(transaction.booking.attributes.start);
  const end = moment(transaction.booking.attributes.end);

  const durationInHours = end.diff(start, 'hours'); // only full hours
  return durationInHours;
};
export const isBookingEnded = booking => {
  if (!booking || !booking.attributes || !booking.attributes.end) {
    return false; // If booking or end time is missing, treat as not ended
  }

  const bookingEndTime = new Date(booking.attributes.end).getTime();
  const currentTime = Date.now();

  return bookingEndTime < currentTime;
};

export const getStatusStyle = status => {
  return getBookingStatusStyle(status);
};

export const getPercentageValidators = (minimumPercentage, maximumPercentage, intl) => {
  const percentageRequiredMsgId = { id: 'EditListingPricingAndStockForm.percentageRequired' };
  const percentageRequiredMsg = intl.formatMessage(percentageRequiredMsgId);

  const percentageRangeMsgId = { id: 'EditListingPricingAndStockForm.percentageOutOfRange' };
  const percentageRangeMsg = intl.formatMessage(percentageRangeMsgId, {
    min: minimumPercentage,
    max: maximumPercentage,
  });

  const percentageRangeValidator = value => {
    const numberValue = parseFloat(value);
    if (isNaN(numberValue)) return percentageRangeMsg;
    if (numberValue < minimumPercentage || numberValue > maximumPercentage) {
      return percentageRangeMsg;
    }
    return null;
  };

  return validators.composeValidators(percentageRangeValidator);
};

export const calculateDeposit = (listing, totalPrice, currency) => {
  const { depositPercentage, depositType, depositFixedAmount, isDeposit, advancePricingDetails } =
    listing?.attributes?.publicData || {};
  const securityDeposit = advancePricingDetails?.securityDeposit || {};
  const resolvedDepositType = securityDeposit.type || depositType || 'percent';

  // Convert depositPercentage to a number
  const depositPercentageNumber = parseFloat(securityDeposit.percentage || depositPercentage);
  const fixedDepositAmount = parseFloat(securityDeposit.fixedAmount || depositFixedAmount);

  let depositAmount = null;

  if (isDeposit && resolvedDepositType === 'fixed' && fixedDepositAmount > 0) {
    depositAmount = new Money(Math.round(fixedDepositAmount * 100), currency);
  } else if (totalPrice > 0 && depositPercentageNumber > 0) {
    // Ensure correct rounding to 2 decimals
    const rawAmount = (depositPercentageNumber / 100) * totalPrice;

    // Round to 2 decimals safely
    const roundedAmount = Math.round(rawAmount * 100) / 100;

    depositAmount = new Money(roundedAmount, currency);
  }

  return {
    depositAmount,
    isDeposit,
  };
};

export const cleanPricingDetails = pricingDetails => {
  // Remove empty addons
  if (Array.isArray(pricingDetails.addons) && pricingDetails.addons.length === 0) {
    delete pricingDetails.addons;
  }

  // Filter pricing matrices based on pricing model
  const pricingModel = pricingDetails.pricingModel;
  const pricingMatrices = pricingDetails.pricingMatrices || {};

  if (pricingModel === 'model1') {
    pricingDetails.pricingMatrices = filterPricingMatrix(pricingMatrices, ['PHOTO', 'VIDEO']);
    pricingDetails.baseRates = filterBaseRates(pricingDetails.baseRates, ['PHOTO', 'VIDEO']);
  } else if (pricingModel === 'model2') {
    pricingDetails.pricingMatrices = filterPricingMatrix(pricingMatrices, ['MULTIPURPOSE']);
    pricingDetails.baseRates = filterBaseRates(pricingDetails.baseRates, ['MULTIPURPOSE']);
  }

  // Remove matrices that do not have at least one price field (hourly, halfDay, fullDay) populated
  function filterPricingMatrix(pricingMatrices, allowedKeys) {
    return Object.keys(pricingMatrices)
      .filter(key => allowedKeys.includes(key))
      .reduce((acc, key) => {
        acc[key] = pricingMatrices[key].filter(matrix => {
          // Check if at least one price exists
          return matrix.hourly || matrix.halfDay || matrix.fullDay;
        });
        return acc;
      }, {});
  }

  // Filter base rates based on pricing model
  function filterBaseRates(baseRates, allowedKeys) {
    return Object.keys(baseRates)
      .filter(key => allowedKeys.includes(key))
      .reduce((acc, key) => {
        acc[key] = baseRates[key];
        return acc;
      }, {});
  }

  return pricingDetails;
};

const isTimeWithinRange = (timestamp, startTimeStr, endTimeStr, timeZone) => {
  const time = moment.tz(timestamp, timeZone);

  const year = time.year();
  const month = String(time.month() + 1).padStart(2, '0');
  const date = String(time.date()).padStart(2, '0');
  const dateStr = `${year}-${month}-${date}`;

  const start = moment.tz(`${dateStr} ${startTimeStr}`, 'YYYY-MM-DD HH:mm', timeZone);
  const end = moment.tz(`${dateStr} ${endTimeStr}`, 'YYYY-MM-DD HH:mm', timeZone);

  return time.isSameOrAfter(start) && time.isSameOrBefore(end);
};

export const getAdvancePricingDetails = listing => {
  return listing?.attributes?.publicData?.advancePricingDetails || {};
};

export const getAvailableBookingTypes = listing => {
  return listing?.attributes?.publicData?.bookingType || {};
};

export const getTotalPeopleForBookingType = (pricingMatrices, bookingType) => {
  const pricingArray = pricingMatrices?.[bookingType] || [];

  // Extract max end value from each crewLabel (e.g., "1-5" → 5)
  const maxPeople = pricingArray.reduce((max, item) => {
    const range = item.crewLabel?.split('-');
    const end = parseInt(range?.[1], 10);
    return end > max ? end : max;
  }, 0);

  return maxPeople;
};

export const getRateForSelection = (matrix, crewLabel, durationType) => {
  if (!matrix || !crewLabel || !durationType) return null;
  const entry = matrix.find(p => p.crewLabel === crewLabel);
  return entry ? entry[durationType] : null;
};

export const filterRoutesByAuth = (routes, isAuthenticated, authOnlyPaths) => {
  return routes.filter(route => {
    const isProtected = authOnlyPaths.includes(route.href);
    return isAuthenticated || !isProtected;
  });
};

export const generate24HourTimes = () => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    const formatted = `${hour.toString().padStart(2, '0')}:00`;
    times.push(formatted);
  }
  return times;
};

/**
 * @param {Object} config
 * @param {string[]} config.categoryLevelKeys    e.g. ['categoryLevel1','categoryLevel2']
 * @param {Array<Object>} config.categories      your top-level categories array
 *
 * @param {string} levelKey                      the level you want options for
 * @param {Object} [selected]                    map of already‐chosen ids, keyed by levelKey
 *                                              e.g. { categoryLevel1: 'urban' }
 *
 * @returns {Array<Object>}                      the array of category objects for that level
 */
export const getOptionsForLevel = (config, levelKey, selected = {}) => {
  const { categoryLevelKeys, categories } = config;
  const levelIndex = categoryLevelKeys.indexOf(levelKey);
  if (levelIndex < 0) return [];

  // start at the root categories
  let options = categories;

  // drill down one level for each already‐chosen id up to levelIndex
  for (let i = 0; i < levelIndex; i++) {
    const parentLevelKey = categoryLevelKeys[i];
    const parentId = selected[parentLevelKey];
    if (!parentId) {
      // nothing selected yet at that parent level
      return [];
    }
    const parentCat = options.find(c => c.id === parentId);
    if (!parentCat) {
      return [];
    }
    options = parentCat.subcategories || [];
  }

  return options;
};

export const getPrimaryListingFields = (fields, orderedKeys) => {
  return orderedKeys.map(key => fields.find(f => f.key === key)).filter(Boolean);
};

export function getBookingDurationInHours(startDate, endDate) {
  if (!startDate || !endDate) return 0;

  const start = moment(startDate);
  const end = moment(endDate);

  if (!start.isValid() || !end.isValid()) {
    return 0;
  }

  const duration = moment.duration(end.diff(start));
  const hours = duration.asHours();

  return parseFloat(hours.toFixed(2));
}

export const getAddons = listing => {
  return listing?.attributes?.publicData?.advancePricingDetails?.addons;
};

export const resetBookingFields = (form, values) => {
  // Only reset if values exist, not if they're initial
  if (values.bookingStartTime) form.change('bookingStartTime', '');
  if (values.bookingSlot) form.change('bookingSlot', '');
  if (values.bookingEndTime) form.change('bookingEndTime', '');
  if (values.bookingStartDate) form.change('bookingStartDate', '');
  if (values.bookingEndDate) form.change('bookingEndDate', '');
  if (values.rate) form.change('rate', '');
  if (values.selectedTimeSlots) form.change('selectedTimeSlots', {});
};

export const filterEndTimesByDuration = (availableEndTimes, bookingStartTime, minimumHours) => {
  const startMoment = moment(Number(bookingStartTime));
  // Check if moment is valid
  if (!startMoment.isValid()) {
    return [];
  }

  const minEndMoment = startMoment.clone().add(minimumHours, 'hours');
  const filteredEndTimes = availableEndTimes.filter(slot => {
    return slot.timestamp >= minEndMoment.valueOf();
  });

  // Return the filtered end times
  return filteredEndTimes;
};

export const isAddons = addons => {
  if (addons && Array.isArray(addons)) {
    return addons.every(addon => addon.label && addon.price);
  }
  return false;
};

export function getDurationText(durationType, locale = 'en') {
  const lang = String(locale || 'en').toLowerCase();

  const labels = {
    en: {
      hourly: 'Hourly',
      halfDay: 'Half-day',
      fullDay: 'Full-day',
    },
    th: {
      hourly: 'รายชั่วโมง',
      halfDay: 'ครึ่งวัน',
      fullDay: 'เต็มวัน',
    },
  };

  const dict = labels[lang] || labels.en;
  return dict?.[durationType] || '';
}

export const getLowestPrice = (pricingDetails, bookingType) => {
  if (!pricingDetails || typeof pricingDetails !== 'object') {
    return null; // If pricingDetails is null, undefined, or not an object, return null
  }

  const normalizeBookingTypes = bt => {
    if (Array.isArray(bt)) {
      return bt.filter(t => ['hourly', 'halfDay', 'fullDay'].includes(t));
    }
    if (typeof bt === 'string' && ['hourly', 'halfDay', 'fullDay'].includes(bt)) {
      return [bt];
    }
    return [];
  };

  const allowedTypes = normalizeBookingTypes(bookingType);
  const isAllowed = type => (allowedTypes.length ? allowedTypes.includes(type) : true);

  let lowestPrice = Infinity; // Start with a very high price to ensure it gets replaced.

  // Loop through each category (PHOTO, VIDEO, MULTIPURPOSE)
  Object.values(pricingDetails).forEach(category => {
    if (!Array.isArray(category)) return; // Skip if category is not an array

    category.forEach(pricing => {
      if (!pricing || typeof pricing !== 'object') return; // Skip if pricing is invalid

      // Check all three pricing options and find the smallest non-empty value
      const prices = [
        isAllowed('hourly') && pricing.hourly && parseFloat(pricing.hourly) * 100,
        isAllowed('halfDay') && pricing.halfDay && parseFloat(pricing.halfDay) * 100,
        isAllowed('fullDay') && pricing.fullDay && parseFloat(pricing.fullDay) * 100,
      ].filter(price => !isNaN(price)); // Remove any NaN values

      // Get the minimum value from the array of valid prices
      const minPrice = Math.min(...prices);

      // Update the lowest price if a smaller one is found
      if (minPrice < lowestPrice) {
        lowestPrice = minPrice;
      }
    });
  });

  // Return the lowest price found
  return lowestPrice === Infinity ? null : lowestPrice; // If no valid price was found, return null
};

export const generateCrewRulesFieldProps = (publicData = {}, intl) => {
  const crewRulesPrefix = 'pub_crew_rules_';

  const selectedOptions = crewRules
    .map(rule => crewRulesPrefix + rule.key)
    .filter(fullKey => publicData[fullKey] === 'yes');

  const options = crewRules.map(rule => ({
    key: crewRulesPrefix + rule.key,
    label: intl.formatMessage({ id: rule.label }).replace('allowed', ''),
  }));

  return {
    heading: 'Rules for Crews',
    key: 'rules_for_Crews',
    schemaType: 'multi-enum',
    showUnselectedOptions: true,
    options,
    selectedOptions,
  };
};

export const generateEmailData = (pageData, calculatedOverTimeRate, overtimePeriod) => {
  const { orderData, listing } = pageData || {};
  const { bookingDates, bookingModalDetails } = orderData || {};
  const { publicData = {} } = listing?.attributes || {};
  const { bookingType, durationType, crewSize, isOverTime, rate } = bookingModalDetails || {};
  const start = moment(bookingDates?.bookingStart);
  const end = moment(bookingDates?.bookingEnd);
  const totalNumberOfHours = calculateQuantityFromHours(
    bookingDates?.bookingStart,
    bookingDates?.bookingEnd
  );
  const totalListingPrice = (Number(rate) * Number(totalNumberOfHours)).toFixed(2);

  const formatTime = m => m.format('hA'); // e.g., 12AM, 2PM

  const imageUrl = listing?.images?.[0]?.attributes?.variants?.['default']?.url || '';

  const emailData = {
    purpose:
      bookingType === 'PHOTO'
        ? 'Photo Shoot'
        : bookingType === 'VIDEO'
        ? 'Video Shoot'
        : 'Multipurpose',
    bookingType:
      durationType === 'hourly' ? 'Hourly' : durationType === 'halfDay' ? 'Half-day' : 'Full-day',
    crewSize: crewSize || 'N/A',
    day: start.format('dddd'), // e.g., Tuesday
    date: start.format('MMM DD, YYYY').toUpperCase(), // e.g., JUN 24, 2025
    bookingTime: `${formatTime(start)} - ${formatTime(end)}`,
    overTime: 'N/A',
    address: publicData?.location?.address || 'N/A',
    image: imageUrl,
    overTimeTxt: `Overtime ${overtimePeriod}`,
    overTimePrice: `${Number(calculatedOverTimeRate).toFixed(2)}`,
    isOverTime,
    totalListingPrice,
  };

  return emailData;
};

// Helper function to filter available times based on duration type
export const filterAvailableTimes = (
  availableStartTimes,
  advancePricingDetails,
  durationType,
  timeZone
) => {
  if (!availableStartTimes || !availableStartTimes.length) return [];
  if (!advancePricingDetails) return availableStartTimes;
  if (!timeZone) return availableStartTimes;

  const { halfDayTimes = {}, fullDayTimes = {} } = advancePricingDetails;
  const { morningStart, morningEnd, afternoonStart, afternoonEnd } = halfDayTimes;
  const { start: fullStart, end: fullEnd } = fullDayTimes;

  const isWithinWindow = (slotTs, windowStartHHmm, windowEndHHmm) => {
    if (!windowStartHHmm || !windowEndHHmm) return false;

    const slotM = moment.tz(Number(slotTs), timeZone);
    const dateStr = slotM.format('YYYY-MM-DD');

    const startM = moment.tz(`${dateStr} ${windowStartHHmm}`, 'YYYY-MM-DD HH:mm', timeZone);
    let endM = moment.tz(`${dateStr} ${windowEndHHmm}`, 'YYYY-MM-DD HH:mm', timeZone);

    // overnight windows, just in case
    if (endM.isSameOrBefore(startM)) endM = endM.add(1, 'day');

    return slotM.isSameOrAfter(startM) && slotM.isBefore(endM);
  };

  if (durationType === DURATION_TYPE.FULL_DAY) {
    if (!fullStart || !fullEnd) return availableStartTimes;

    return availableStartTimes.filter(slot => {
      const slotM = moment.tz(Number(slot.timestamp), timeZone);
      const dateStr = slotM.format('YYYY-MM-DD');
      const earliestFullStart = moment.tz(`${dateStr} ${fullStart}`, 'YYYY-MM-DD HH:mm', timeZone);
      return slotM.isSameOrAfter(earliestFullStart);
    });
  }

  if (durationType === DURATION_TYPE.HALF_DAY) {
    if (!morningStart || !morningEnd || !afternoonStart || !afternoonEnd)
      return availableStartTimes;

    return availableStartTimes.filter(slot => {
      const ts = Number(slot.timestamp);
      return (
        isWithinWindow(ts, morningStart, morningEnd) ||
        isWithinWindow(ts, afternoonStart, afternoonEnd)
      );
    });
  }

  return availableStartTimes;
};

export const createAvailableDaySlots = (
  advancePricingDetails,
  availableStartTimes,
  durationType,
  timeZone
) => {
  const { halfDayTimes = {}, fullDayTimes = {} } = advancePricingDetails || {};
  const { morningStart, morningEnd, afternoonStart, afternoonEnd } = halfDayTimes || {};
  const { start: fullStart, end: fullEnd } = fullDayTimes || {};

  if (!isArrayLength(availableStartTimes) || !timeZone) return [];

  const toLabelHHmm = ts => moment.tz(Number(ts), timeZone).format('h:mm a'); // 10:00 am

  // Find exact start time match (HH:mm in listing TZ)
  const findExactStartTime = windowStartHHmm => {
    return (
      availableStartTimes.find(slot => {
        const slotM = moment.tz(Number(slot.timestamp), timeZone);
        return slotM.format('HH:mm') === windowStartHHmm;
      }) || null
    );
  };

  // Detect smallest step (60m, 30m, etc) from availableStartTimes
  const getStepMinutes = () => {
    if (availableStartTimes.length < 2) return 60;
    const sorted = [...availableStartTimes]
      .map(s => Number(s.timestamp))
      .filter(Boolean)
      .sort((a, b) => a - b);

    let minDiff = Infinity;
    for (let i = 1; i < sorted.length; i++) {
      const d = sorted[i] - sorted[i - 1];
      if (d > 0) minDiff = Math.min(minDiff, d);
    }
    const minutes = Math.max(1, Math.round(minDiff / (60 * 1000)));
    return Number.isFinite(minutes) ? minutes : 60;
  };

  /**
   * Ensure the entire window is available.
   * Works with 60m and 30m increments (or whatever the smallest step is).
   */
  const isEntireSlotAvailable = (windowStartHHmm, windowEndHHmm) => {
    const firstSlotTs = Number(availableStartTimes[0].timestamp);
    const dateStr = moment.tz(firstSlotTs, timeZone).format('YYYY-MM-DD');

    const windowStart = moment.tz(`${dateStr} ${windowStartHHmm}`, 'YYYY-MM-DD HH:mm', timeZone);
    let windowEnd = moment.tz(`${dateStr} ${windowEndHHmm}`, 'YYYY-MM-DD HH:mm', timeZone);
    if (windowEnd.isSameOrBefore(windowStart)) windowEnd = windowEnd.add(1, 'day');

    const stepMinutes = getStepMinutes();

    const availableSet = new Set(
      availableStartTimes.map(s =>
        moment.tz(Number(s.timestamp), timeZone).format('YYYY-MM-DD HH:mm')
      )
    );

    const cursor = windowStart.clone();
    while (cursor.isBefore(windowEnd)) {
      if (!availableSet.has(cursor.format('YYYY-MM-DD HH:mm'))) return false;
      cursor.add(stepMinutes, 'minutes');
    }

    return true;
  };

  const slots = [];

  const pushSlot = (windowStartHHmm, windowEndHHmm) => {
    // only add if full window is available
    if (!isEntireSlotAvailable(windowStartHHmm, windowEndHHmm)) return;

    const candidate = findExactStartTime(windowStartHHmm);
    if (!candidate) return;

    const startM = moment.tz(Number(candidate.timestamp), timeZone);
    const dateStr = startM.format('YYYY-MM-DD');

    let endM = moment.tz(`${dateStr} ${windowEndHHmm}`, 'YYYY-MM-DD HH:mm', timeZone);
    if (endM.isBefore(startM)) endM = endM.add(1, 'day');

    const endTs = endM.valueOf();

    const label = `${toLabelHHmm(candidate.timestamp)} - ${moment
      .tz(endTs, timeZone)
      .format('h:mm a')}`;

    slots.push({
      start: { timestamp: Number(candidate.timestamp) },
      end: { timestamp: endTs },
      slot: label,
    });
  };

  if (durationType === DURATION_TYPE.HALF_DAY) {
    if (morningStart && morningEnd) pushSlot(morningStart, morningEnd);
    if (afternoonStart && afternoonEnd) pushSlot(afternoonStart, afternoonEnd);
  } else if (durationType === DURATION_TYPE.FULL_DAY && fullStart && fullEnd) {
    pushSlot(fullStart, fullEnd);
  }

  return slots;
};

// Wrapper function to filter and create slots based on duration type
export const createFilteredDaySlots = (
  advancePricingDetails,
  availableStartTimes,
  durationType,
  timeZone
) => {
  const filteredTimes = filterAvailableTimes(
    availableStartTimes,
    advancePricingDetails,
    durationType,
    timeZone
  );
  return createAvailableDaySlots(advancePricingDetails, filteredTimes, durationType, timeZone);
};

export const filterTimesByDuration = (times, durationType, selectedSlots, timeZone) => {
  if (['halfDay', 'fullDay'].includes(durationType) && selectedSlots?.start && selectedSlots?.end) {
    return times.filter(p =>
      isTimeWithinRange(p.timestamp, selectedSlots.start, selectedSlots.end, timeZone)
    );
  }
  return times;
};

export const removeExactDuplicates = fields => {
  const seen = new Set();
  return fields.filter(field => {
    const identifier = `${field.key}|${field.label}|${field.value}`;
    if (seen.has(identifier)) return false;
    seen.add(identifier);
    return true;
  });
};

export const calculateTimeDifference = (start, end) => {
  if (!start || !end) return 0;

  try {
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);

    if (isNaN(startHour) || isNaN(startMin) || isNaN(endHour) || isNaN(endMin)) {
      return 0;
    }

    const startTime = startHour + startMin / 60;
    let endTime = endHour + endMin / 60;

    // Handle cases where end time is on the next day (e.g., 22:00 to 02:00)
    if (endTime < startTime) {
      endTime += 24;
    }

    return endTime - startTime;
  } catch (error) {
    console.error('Error in calculateTimeDifference:', error);
    return 0;
  }
};

export const getExtraTimeSlots = (selectedTimeSlots, fullDayTimes, timeZone) => {
  if (!selectedTimeSlots || !fullDayTimes || !fullDayTimes.start || !fullDayTimes.end) {
    console.warn('Invalid inputs for getExtraTimeSlots:', { selectedTimeSlots, fullDayTimes });
    return 0;
  }

  const fullDayDuration = calculateTimeDifference(fullDayTimes.start, fullDayTimes.end);
  if (fullDayDuration <= 0) {
    console.warn('Invalid full day duration:', fullDayDuration);
    return 0;
  }
  let totalHours = 0;
  selectedTimeSlots.forEach(slot => {
    const startMoment = moment(fullDayTimes.start, 'HH:mm');
    let endSlotTime = moment(slot.attributes.end)
      .tz(timeZone)
      .format('HH:mm');

    if (endSlotTime === '00:00') {
      endSlotTime = '24:00';
    }

    const endMoment = moment(endSlotTime, 'HH:mm');

    const diffInMinutes = endMoment.diff(startMoment, 'minutes');
    const diffInHours = diffInMinutes / 60;
    totalHours += diffInHours;
  });

  // Calculate extra hours
  const extraHours = totalHours - fullDayDuration;
  return extraHours;
};

export function formatOpeningHours(data) {
  return data.entries
    .map(entry => {
      const dayOfWeekMap = {
        mon: 'Mon',
        tue: 'Tue',
        wed: 'Wed',
        thu: 'Thu',
        fri: 'Fri',
        sat: 'Sat',
        sun: 'Sun',
      };

      const formattedDay = dayOfWeekMap[entry.dayOfWeek.toLowerCase()] || entry.dayOfWeek;
      return `${formattedDay}  ${entry.startTime} AM - ${entry.endTime} PM`;
    })
    .join(' | ');
}

export const normalizeLongAndEnumFields = (propsForCustomFields, listingFields) => {
  return propsForCustomFields.map(field => {
    const { key, schemaType, heading, text } = field;

    if (schemaType === 'long') {
      return {
        heading,
        key,
        schemaType,
        text,
      };
    }

    if (schemaType === 'enum') {
      const config = listingFields.find(f => f.key === key);
      const enumOptions = config?.enumOptions || [];
      const options = enumOptions.map(opt => ({
        key: opt.option,
        label: opt.label,
      }));

      const selectedLabel = enumOptions.find(opt => opt.option === text)?.label;
      return {
        heading,
        key,
        schemaType,
        options,
        selectedOptions: selectedLabel ? [selectedLabel] : [],
        showUnselectedOptions: false,
      };
    }

    return field; // unchanged for other schemaTypes
  });
};

export function getSinglePricingLabel(pricingData, bookingType) {
  // Guard: pricingData must be an object
  const data = pricingData && typeof pricingData === 'object' ? pricingData : {};

  const CATEGORIES = ['PHOTO', 'VIDEO', 'MULTIPURPOSE'];

  // Build a flat list of entries; ignore missing/non-array categories safely
  const allEntries = CATEGORIES.reduce((acc, cat) => {
    const arr = Array.isArray(data?.[cat]) ? data[cat] : [];
    // filter out null/undefined entries
    return acc.concat(arr.filter(Boolean));
  }, []);

  if (!allEntries.length) return '';

  const toNumber = v => {
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
  };

  // Collect valid (>0) numeric rates for each key
  const getValidRates = key =>
    allEntries.map(e => toNumber(e?.[key])).filter(n => Number.isFinite(n) && n > 0);

  const formatPrice = price =>
    `฿${price.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 20,
    })}`;

  const normalizeBookingTypes = bt => {
    if (Array.isArray(bt)) {
      return bt.filter(t => ['hourly', 'halfDay', 'fullDay'].includes(t));
    }
    if (typeof bt === 'string' && ['hourly', 'halfDay', 'fullDay'].includes(bt)) {
      return [bt];
    }
    return [];
  };

  const allowedTypes = normalizeBookingTypes(bookingType);
  const isAllowed = type => (allowedTypes.length ? allowedTypes.includes(type) : true);

  const hourlyRates = isAllowed('hourly') ? getValidRates('hourly') : [];
  const halfDayRates = isAllowed('halfDay') ? getValidRates('halfDay') : [];
  const fullDayRates = isAllowed('fullDay') ? getValidRates('fullDay') : [];

  const hasHourly = hourlyRates.length > 0;
  const hasHalfDay = halfDayRates.length > 0;
  const hasFullDay = fullDayRates.length > 0;

  // Prefer the explicitly requested bookingType when available
  if (bookingType === 'hourly' && hasHourly) return `${formatPrice(Math.min(...hourlyRates))}/hour`;
  if (bookingType === 'halfDay' && hasHalfDay)
    return `${formatPrice(Math.min(...halfDayRates))}/half-day`;
  if (bookingType === 'fullDay' && hasFullDay)
    return `${formatPrice(Math.min(...fullDayRates))}/full-day`;

  // Fallbacks in priority order
  if (hasHourly && bookingType?.includes('hourly'))
    return `${formatPrice(Math.min(...hourlyRates))}/hour`;
  if (hasHalfDay && bookingType?.includes('halfDay'))
    return `${formatPrice(Math.min(...halfDayRates))}/half day`;
  if (hasFullDay && bookingType?.includes('fullDay'))
    return `${formatPrice(Math.min(...fullDayRates))}/full day`;

  return '';
}

export const formatTimeTo12Hour = (time24, intl) => {
  if (!time24) return '';
  const [hourStr, minuteStr] = time24.split(':');

  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  if (Number.isNaN(hour) || Number.isNaN(minute)) return time24;

  const isPM = hour >= 12;

  hour = hour % 12 || 12;

  const amLabel = intl?.formatMessage?.({ id: 'Time.am' }) ?? 'AM';
  const pmLabel = intl?.formatMessage?.({ id: 'Time.pm' }) ?? 'PM';

  return `${hour}:${String(minute).padStart(2, '0')} ${isPM ? pmLabel : amLabel}`;
};

export function getWelcomeGuide(listing) {
  if (listing?.attributes?.publicData?.welcomeGuide) {
    return listing.attributes?.publicData?.welcomeGuide;
  }
  return null;
}

export function getBookingTypes(enabledRates) {
  const types = [];

  if (enabledRates?.hourly) {
    types.push('hourly');
  }
  if (enabledRates?.halfDay) {
    types.push('halfDay');
  }
  if (enabledRates?.fullDay) {
    types.push('fullDay');
  }

  return types;
}

export function getBookingTotalPeriod(transaction) {
  const bookingStart = moment(transaction?.booking?.attributes?.start);
  let finalEnd = moment(transaction?.booking?.attributes?.end);

  // Look for overtime end time in lineItems
  transaction?.attributes?.lineItems?.forEach(item => {
    if (item.code?.includes('overtime')) {
      // Example: "line-item/overtime  4:00 pm -- 7:00 pm"
      const match = item.code.match(/(\d{1,2}:\d{2}\s*(am|pm))\s*--\s*(\d{1,2}:\d{2}\s*(am|pm))/i);
      if (match) {
        const overtimeEndTime = match[3]; // e.g., "7:00 pm"
        const bookingDate = bookingStart.format('YYYY-MM-DD');
        const overtimeEnd = moment(`${bookingDate} ${overtimeEndTime}`, 'YYYY-MM-DD h:mm a');

        if (overtimeEnd.isAfter(finalEnd)) {
          finalEnd = overtimeEnd;
        }
      }
    }
  });

  const totalHours = moment.duration(finalEnd.diff(bookingStart)).asHours();

  const startFormatted = bookingStart.format('h:mm a');
  const endFormatted = finalEnd.format('h:mm a');

  return {
    timeRange: `${startFormatted} - ${endFormatted}`,
    totalHours,
  };
}

export const copyToClipboard = async text => {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus({
      preventScroll: true,
    });
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  } catch (e) {
    console.error(`Copying failed with error: ${e}`);
    throw e;
  }
};

export function getTrimmedAddress(fullAddress) {
  if (!fullAddress || typeof fullAddress !== 'string') return '';

  const parts = fullAddress.split(',').map(part => part.trim());

  // If there are at least 4 parts, remove the first two (e.g., street and neighborhood)
  if (parts.length >= 4) {
    return parts.slice(2).join(', ');
  }

  // If there are at least 3 parts, remove the first one
  if (parts.length >= 3) {
    return parts.slice(1).join(', ');
  }

  // Fallback to return original if unexpected format
  return fullAddress;
}

export const getMinimumBookingSentence = (intl, minimumHours) => {
  const hours = Number(minimumHours) || 0;

  if (!hours) return null;

  return intl.formatMessage(
    {
      id: 'booking.minimumBookingSentence',
      defaultMessage: 'Minimum booking period is {hours} {unit} for this listing.',
    },
    {
      hours,
      unit: hours === 1 ? 'hr' : 'hrs',
    }
  );
};

export const getMinBookingLabel = (intl, minimumHours, bookingType) => {
  // Validate bookingType input
  if (!Array.isArray(bookingType) || bookingType.length === 0) {
    return intl.formatMessage({
      id: 'booking.noOptions',
      defaultMessage: 'No booking options available',
    });
  }

  // Filter valid booking types
  const validTypes = bookingType.filter(type => ['hourly', 'halfDay', 'fullDay'].includes(type));

  // If no valid types, return default message
  if (validTypes.length === 0) {
    return intl.formatMessage({
      id: 'booking.noOptions',
      defaultMessage: 'No booking options available',
    });
  }

  // Check for hourly availability
  if (validTypes.includes('hourly')) {
    return intl.formatMessage(
      {
        id: 'booking.hourly',
        defaultMessage: '{hours} {unit}. minimum',
      },
      {
        hours: minimumHours,
        unit: minimumHours === 1 ? 'hr' : 'hrs',
      }
    );
  }

  // Check for halfDay availability (when hourly is not available)
  if (validTypes.includes('halfDay')) {
    return intl.formatMessage({
      id: 'booking.halfDay',
      defaultMessage: 'Half Day minimum',
    });
  }

  // If only fullDay is available
  if (validTypes.includes('fullDay')) {
    return intl.formatMessage({
      id: 'booking.fullDay',
      defaultMessage: 'Full Day minimum',
    });
  }

  // Fallback for unexpected cases
  return intl.formatMessage({
    id: 'booking.noOptions',
    defaultMessage: 'No booking options available',
  });
};

export const extractLowestPriceFromMatrix = values => {
  const { pricingDetails, bookingType } = values || {};
  if (!pricingDetails || !bookingType || bookingType.length === 0) return 0;

  const { pricingMatrices } = pricingDetails;
  if (!pricingMatrices) return 0;

  const allPrices = [];

  bookingType.forEach(type => {
    const matrix = pricingMatrices?.PHOTO || [];
    matrix.forEach(row => {
      const price = row[type];
      if (price && price !== '-' && !isNaN(price)) {
        allPrices.push(parseFloat(price));
      }
    });
  });

  return allPrices.length > 0 ? Math.min(...allPrices) : 0;
};

export const getLineTotalByCode = (lineItems, code) => {
  const lineItem = isArrayLength(lineItems) && lineItems?.find(item => item.code === code);
  if (lineItem) {
    return lineItem.lineTotal;
  }
  return null; // Return null if the item is not found
};

export const getSearchParams = () => {
  return {
    page: 1,
    perPage: 100,
    include: ['author', 'images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'createdAt',
      'deleted',
      'state',
      'metadata',
      'publicData',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': [
      'variants.scaled-xlarge',
      'variants.scaled-xlarge-2x',
      'variants.scaled-xlarge-6x',
    ],
    'limit.images': 4,
  };
};

export const filterOutBlogSection = sections => {
  if (!Array.isArray(sections)) return [];

  return sections.filter(section => section.sectionName !== 'Blog');
};

export function isFunction(value) {
  return typeof value === 'function';
}

export const useStateRef = initialState => {
  const [state, setState] = useState(initialState);
  const ref = useRef(initialState);

  const dispatch = useCallback(stateToSet => {
    ref.current = isFunction(stateToSet) ? stateToSet(ref.current) : stateToSet;
    setState(ref.current);
  }, []);

  return [state, dispatch, ref];
};

export const getTransformedImages = images => {
  if (!isArrayLength(images) || images.length === 0) {
    return [];
  }

  const transformed = images
    .map(item => {
      if (typeof item === 'string') {
        return new UUID(item);
      }
      if (item && item.imageId && item.imageId.uuid) {
        return new UUID(item.imageId.uuid);
      }
      if (item && item.id && item.id.uuid) {
        return new UUID(item.id.uuid);
      }
      return null;
    })
    .filter(Boolean);

  return transformed;
};

export function getRefundAmount(transaction) {
  if (!transaction?.attributes) return null;

  const { payinTotal, lineItems } = transaction.attributes || {};
  if (!payinTotal || !lineItems) return null;

  // Find the customer-commission line item
  const customerCommission = lineItems.find(li => li.code === 'line-item/customer-commission');

  const payinAmount = payinTotal.amount || 0;
  const commissionAmount = customerCommission?.lineTotal?.amount || 0;
  const amount = payinAmount - commissionAmount;
  return amount;
}

export const customerCancelTests = {
  within24h_gt8d: '2025-08-29T10:00:00+05:30', // ~18h after creation; ~12 days pre-event -> 100%
  gt30d: '2025-07-31T12:00:00+05:30', // >30d pre-event -> 100%
  gt7d: '2025-09-02T10:00:00+05:30', // ~8 days pre-event -> 50%
  lt7d: '2025-09-05T10:00:00+05:30', // ~5 days pre-event -> 0%
};

export const providerCancelTests = {
  within48h_gt8d: '2025-08-29T12:00:00+05:30',
  // >7 days before event (and ≤30) → Orange
  gt7d: '2025-09-03T10:00:00+05:30',

  // ≤7 days before event → Red
  le7d: '2025-09-10T10:00:00+05:30',

  // at/after event start → Red (edge case)
  afterEvent: '2025-09-15T12:00:00+05:30',
};

const toZonedMoment = v => {
  if (!v) return null;
  if (moment.isMoment(v)) return v;
  if (v instanceof Date) return moment(v);
  const m = moment.parseZone(v);
  return m.isValid() ? m : null;
};

const parseMoneyToSubunits = (value, fallbackCurrency = 'THB') => {
  // Accepts: "THB 625.00", "-THB 625.00", "625.00", 625, 62500
  if (value == null) return { amount: 0, currency: fallbackCurrency };

  // If already a number:
  if (typeof value === 'number') {
    // Heuristic:
    // - if it's big, assume subunits already
    // - if it's small, assume major units
    const amount = value > 10000 ? Math.round(value) : Math.round(value * 100);
    return { amount, currency: fallbackCurrency };
  }

  const str = String(value).trim();

  // Try to extract currency (first 3 letters)
  const curMatch = str.match(/([A-Z]{3})/);
  const currency = curMatch?.[1] || fallbackCurrency;

  // Extract number (keep sign)
  const numMatch = str.match(/-?\d[\d,]*\.?\d*/);
  const numStr = (numMatch?.[0] || '0').replace(/,/g, '');
  const major = Number(numStr) || 0;

  // Convert to subunits
  const subunits = Math.round(Math.abs(major) * 100);

  return { amount: subunits, currency };
};

export const buildRefundObjFromCancelOrMeta = transaction => {
  const meta = transaction?.attributes?.metadata || {};
  const payinCurrency = transaction?.attributes?.payinTotal?.currency || 'THB';

  // 1) Prefer metadata.refundObj if present (it usually has the intended refund logic)
  const metaRefund = meta?.refundObj;
  if (metaRefund) {
    // Try best-effort amount
    const { amount, currency } = parseMoneyToSubunits(
      metaRefund.refundAmount != null ? metaRefund.refundAmount : metaRefund.formatedRefundAmount,
      metaRefund.currency || payinCurrency
    );

    return {
      refundAmount: amount,
      formatedRefundAmount:
        metaRefund.formatedRefundAmount || `${currency} ${(amount / 100).toFixed(2)}`,
      refundPercent: metaRefund.refundPercent ?? 0,
      hoursFromBooking: metaRefund.hoursFromBooking,
      daysToEvent: metaRefund.daysToEvent,
      reason: metaRefund.reason,
      breakdown: metaRefund.breakdown || {
        masterMerchantPercent: 10,
        subMerchantPercent: 90,
        bookerPercent: 0,
      },
      textMessage: metaRefund.textMessage,
      textMessage2: metaRefund.textMessage2,
      flag: metaRefund.flag,
    };
  }

  // 2) Fallback: metadata.cancelObj
  const cancelObj = meta?.cancelObj;
  if (cancelObj) {
    // cancelObj.refundAmount looks like "-THB 625.00" in your logs
    const { amount, currency } = parseMoneyToSubunits(cancelObj.refundAmount, payinCurrency);

    return {
      refundAmount: amount,
      formatedRefundAmount:
        cancelObj.formatedRefundAmount || `${currency} ${(amount / 100).toFixed(2)}`,
      refundPercent: 0, // cancelObj usually doesn't store percent; keep 0 unless you also store it
      reason: cancelObj.reason || 'Refund calculated from cancellation snapshot',
      breakdown: cancelObj.breakdown || {
        masterMerchantPercent: 10,
        subMerchantPercent: 90,
        bookerPercent: 0,
      },
      textMessage: cancelObj.textMessage,
      textMessage2: cancelObj.textMessage2,
      // (optional extras if your RefundBreakdown uses them)
      customerTotalPrice: cancelObj.customerTotalPrice,
      providerAmount: cancelObj.providerAmount,
      serviceFee: cancelObj.serviceFee,
      paymentFee: cancelObj.paymentFee,
      totalUnitPrice: cancelObj.totalUnitPrice,
      unitItem: cancelObj.unitItem,
    };
  }

  // 3) Nothing found
  return null;
};

export function calculateCustomerRefund(
  transaction,
  cancelDate,
  transitionName,
  isReschdule, // keep your param spelling to avoid breaking callers
  lastTransition,
  previouslyRescheduled = false
) {
  const bookingStart = toZonedMoment(transaction?.booking?.attributes?.start);
  const createdAt = toZonedMoment(transaction?.attributes?.createdAt);
  const cancelAt = toZonedMoment(cancelDate || new Date());
  const currency = transaction?.attributes?.payinTotal?.currency || 'THB';

  const payinTotal = Number(transaction?.attributes?.payinTotal?.amount || 0); // subunits
  const refundCustomerCommission = transaction?.attributes?.lineItems?.find(
    item => item.code === LINE_ITEM_CUSTOMER_COMMISSION
  );
  const refundCustomerOmiseFee = transaction?.attributes?.lineItems?.find(
    item => item.code === LINE_ITEM_OMISE_FEE
  );

  const totalFeeAmount =
    Math.abs(refundCustomerCommission?.lineTotal?.amount || 0) +
    Math.abs(refundCustomerOmiseFee?.lineTotal?.amount || 0);

  // For partial refunds we exclude fees; for special 100% cases we use full base.
  const customBaseAmount = Math.max(0, payinTotal - totalFeeAmount);

  // --- helper: always return a consistent formatted amount string
  const makeFormatted = amountSubunits => {
    // If you already have a formatNumber util, keep using it:
    try {
      return `${currency} ${formatNumber(amountSubunits / 100)}`;
    } catch {
      // Fallback: add thousands separators to subunits
      const n = Number.isFinite(amountSubunits / 100) ? amountSubunits / 100 : 0;
      return `${currency} ${n.toLocaleString(undefined)}`;
    }
  };

  // Cancel after reschedule ⇒ no refund
  if (isReschdule) {
    const refundAmount = 0;
    return {
      refundAmount,
      formatedRefundAmount: makeFormatted(refundAmount),
      refundPercent: 0,
      hoursFromBooking: 'cancelAfterRescheduleCase',
      daysToEvent: 'cancelAfterRescheduleCase',
      reason: 'After rescheduling, you are no longer eligible for cancellation or refund.',
      breakdown: { masterMerchantPercent: 10, subMerchantPercent: 90, bookerPercent: 0 },
      textMessage: 'RefundForm.rescheduleNoRefundMessage',
    };
  }

  if (transitionName === TRANSITIONS.DECLINE || transitionName === TRANSITIONS.DECLINE_PAY_LATER) {
    const refundAmount = 0;
    return {
      refundAmount,
      formatedRefundAmount: makeFormatted(refundAmount),
      refundPercent: 0,
      hoursFromBooking: 'declineCase',
      daysToEvent: 'declineCase',
      reason: 'Declined booking (No refund)',
      breakdown: { masterMerchantPercent: 10, subMerchantPercent: 90, bookerPercent: 0 },
    };
  }

  // Guard: invalid dates
  if (!bookingStart || !createdAt || !cancelAt) {
    const refundAmount = 0;
    return {
      refundAmount,
      refundPercent: 0,
      formatedRefundAmount: makeFormatted(refundAmount),
      reason: 'Invalid booking or cancellation dates',
      breakdown: { masterMerchantPercent: 0, subMerchantPercent: 0, bookerPercent: 0 },
    };
  }

  // After event start ⇒ no refund
  if (cancelAt.isSameOrAfter(bookingStart)) {
    const refundAmount = 0;
    return {
      refundAmount,
      refundPercent: 0,
      formatedRefundAmount: makeFormatted(refundAmount),
      reason: 'Cancellation after event start (No refund)',
      breakdown: { masterMerchantPercent: 10, subMerchantPercent: 90, bookerPercent: 0 },
      textMessage: 'RefundForm.noRefundMessage',
    };
  }

  // Previously rescheduled ⇒ no refund
  if (previouslyRescheduled) {
    const refundAmount = 0;
    return {
      refundAmount,
      refundPercent: 0,
      formatedRefundAmount: makeFormatted(refundAmount),
      reason: 'Booking was rescheduled (No refund)',
      breakdown: { masterMerchantPercent: 10, subMerchantPercent: 90, bookerPercent: 0 },
    };
  }

  const hoursFromBooking = Math.max(0, cancelAt.diff(createdAt, 'hours', true));
  const hoursToEvent = bookingStart.diff(cancelAt, 'hours', true);
  const daysToEvent = hoursToEvent / 24;

  let refundPercent = 0;
  let reason = '';
  let breakdown = {};
  let textMessage;
  let textMessage2;

  // Policy boundary:
  // - less than 7 days (< 168h): no refund
  // - 7 days or more (>= 168h): allowed
  //
  // Case 1: <=24h since booking & >=8 days before event
  if (hoursFromBooking <= 24 && hoursToEvent >= 8 * 24) {
    refundPercent = 100;
    reason = 'Cancelled within 24 hours of booking and at least 8 days before event (100% refund)';
    breakdown = { masterMerchantPercent: 10, subMerchantPercent: 0, bookerPercent: 100 };
    textMessage = 'RefundForm.noRefundServiceAndPlatfromFeeMessage';
    textMessage2 = 'RefundForm.rentalFeeRefundMessage';
  }
  // Case 2: >=30 days before event
  else if (hoursToEvent >= 30 * 24) {
    refundPercent = 100;
    reason = 'Cancelled at least 30 days before event (100% refund)';
    breakdown = { masterMerchantPercent: 10, subMerchantPercent: 0, bookerPercent: 100 };
    textMessage = 'RefundForm.noRefundServiceAndPlatfromFeeMessage';
    textMessage2 = 'RefundForm.rentalFeeRefundMessage';
  }
  // Case 3: >=7 days before event
  else if (hoursToEvent >= 7 * 24) {
    refundPercent = 50;
    reason = 'Cancelled at least 7 days before event (50% refund)';
    breakdown = { masterMerchantPercent: 5, subMerchantPercent: 45, bookerPercent: 50 };
    textMessage = 'RefundForm.noRefundServiceAndPlatfromFeeMessage';
  }
  // Case 4: <7 days before event
  else {
    // You had a special branch with lastTransition; keep it if you need,
    // but it should NOT flip to 100% when <7 days (your reason says no refund).
    refundPercent = 0;
    reason = 'Cancelled less than 7 days before event (No refund)';
    breakdown = { masterMerchantPercent: 10, subMerchantPercent: 90, bookerPercent: 0 };
    textMessage = 'RefundForm.noRefundMessage';
  }

  // For partial/standard refunds, use customBaseAmount (excluding fees)
  const refundAmount = Math.round((customBaseAmount * refundPercent) / 100);
  return {
    refundAmount,
    formatedRefundAmount: makeFormatted(refundAmount),
    refundPercent,
    hoursFromBooking,
    daysToEvent,
    reason,
    breakdown,
    textMessage,
    textMessage2,
  };
}

export function calculateProviderRefund(transaction, cancelDate = moment(), isReschdule) {
  const bookingStart = toZonedMoment(transaction?.booking?.attributes?.start);
  const createdAt = toZonedMoment(transaction?.attributes?.createdAt);
  const cancelAt = toZonedMoment(cancelDate || new Date());
  const currency = transaction?.attributes?.payoutTotal?.currency || 'THB';

  const payinTotal = Number(transaction?.attributes?.payinTotal?.amount || 0);
  const refundCustomerCommission = transaction?.attributes?.lineItems?.find(
    item => item.code === LINE_ITEM_CUSTOMER_COMMISSION
  );
  const refundCustomerOmiseFee = transaction?.attributes?.lineItems?.find(
    item => item.code === LINE_ITEM_OMISE_FEE
  );
  const totalFeeAmount =
    Math.abs(refundCustomerCommission?.lineTotal?.amount || 0) +
    Math.abs(refundCustomerOmiseFee?.lineTotal?.amount || 0);
  const customBaseAmount = Math.max(0, payinTotal - totalFeeAmount);

  const refundPercent = 100;
  const refundAmount = Math.round(customBaseAmount);
  const formatedRefundAmount = `${currency} ${formatNumber(refundAmount / 100)}`;

  if (!bookingStart || !createdAt || !cancelAt) {
    return {
      currency,
      refundAmount: baseAmount,
      formatedRefundAmount,
      refundPercent: 100,
      reason: 'Invalid dates provided; defaulting to full refund',
      flag: redFlag,
      breakdown: { masterMerchantPercent: 0, subMerchantPercent: 0, bookerPercent: 100 },
    };
  }

  // ✅ FIX: use hoursToEvent decimal (no floor)
  const hoursFromBooking = cancelAt.diff(createdAt, 'hours', true); // decimal
  const hoursToEvent = bookingStart.diff(cancelAt, 'hours', true); // decimal
  const daysToEvent = hoursToEvent / 24;

  // Default
  let flag = redFlag;
  let reason = 'Cancelled less than 7 days before event';

  if (cancelAt.isSameOrAfter(bookingStart)) {
    flag = redFlag;
    reason = 'Cancelled at/after event start';
  } else if (hoursFromBooking <= 24 && hoursToEvent >= 8 * 24) {
    flag = yellowFlag;
    reason = 'Cancelled within 24 hours of booking and at least 8 days before event';
  } else if (hoursToEvent >= 30 * 24) {
    flag = yellowFlag;
    reason = 'Cancelled at least 30 days before event';
  } else if (hoursToEvent >= 7 * 24) {
    flag = orangeFlag;
    reason = 'Cancelled at least 7 days before event';
  }

  if (isReschdule) {
    return {
      currency,
      formatedRefundAmount,
      refundAmount: 0,
      refundPercent: 0,
      hoursFromBooking,
      daysToEvent,
      reason: 'Cancel after reschedule booking accept (No refund).',
      flag,
      breakdown: { masterMerchantPercent: 10, subMerchantPercent: 90, bookerPercent: 0 },
      textMessage: 'RefundForm.rescheduleNoRefundMessage',
    };
  }

  return {
    currency,
    refundAmount,
    formatedRefundAmount,
    refundPercent,
    hoursFromBooking,
    daysToEvent,
    reason,
    flag,
    breakdown: { masterMerchantPercent: 10, subMerchantPercent: 0, bookerPercent: 100 },
  };
}

export const hasAcceptedReschedule = tx =>
  tx?.some(t => t.transition === transitions.ACCEPT_RESCHEDULE_BOOKING);

export const getCustomerCommissionPercentage = (lineItems = []) => {
  const item = lineItems.find(li => li.code === 'line-item/customer-commission');
  if (!item || !item.percentage) return null;

  return typeof item.percentage.toNumber === 'function'
    ? item.percentage.toNumber()
    : Number(item.percentage);
};

export const getProviderCommissionPercentage = (lineItems = []) => {
  const item = lineItems.find(li => li.code === 'line-item/provider-commission');
  if (!item || !item.percentage) return null;

  return typeof item.percentage.toNumber === 'function'
    ? item.percentage.toNumber()
    : Number(item.percentage);
};

export const getAllFeeAmounts = lineItems => {
  const refundProviderCommission = lineItems.find(
    item => item.code === LINE_ITEM_PROVIDER_COMMISSION && item.reversal
  );
  const refundCustomerCommission = lineItems.find(
    item => item.code === LINE_ITEM_CUSTOMER_COMMISSION && item.reversal
  );
  const refundCustomerOmiseFee = lineItems.find(
    item => item.code === LINE_ITEM_OMISE_FEE && item.reversal
  );

  return {
    refundProviderCommission: refundProviderCommission
      ? refundProviderCommission.lineTotal.amount
      : 0,
    refundCustomerCommission: refundCustomerCommission
      ? refundCustomerCommission.lineTotal.amount
      : 0,
    refundCustomerOmiseFee: refundCustomerOmiseFee ? refundCustomerOmiseFee.lineTotal.amount : 0,
  };
};

export const getRecipientId = user => {
  return user?.attributes?.profile?.publicData?.omiseRecipientId || null;
};

export const formatNumberWithSeparators = (value, decimals = 2) => {
  if (value == null || isNaN(value)) return '-';
  return Number(value)
    .toFixed(decimals)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const formatPriceWithSeparators = (value, currency = 'THB') => {
  return `${formatNumberWithSeparators(value)} ${currency}`;
};

export const formatTHBFromCents = cents => {
  if (cents == null || isNaN(cents)) return 'THB -';
  return `THB ${formatNumberWithSeparators(cents / 100, 2)}`;
};

export const clearLaterClickedCookie = storageKey => {
  Cookies.remove(storageKey);
};

export const roundPrice = (amount, decimals = 0) => {
  let numericAmount = amount;

  if (typeof numericAmount === 'string') {
    // strip out non-numeric characters except dot and minus
    const cleaned = numericAmount.replace(/[^\d.-]/g, '');
    numericAmount = parseFloat(cleaned);
  }

  if (typeof numericAmount !== 'number' || isNaN(numericAmount)) {
    return 'THB 0.00';
  }

  const factor = Math.pow(10, decimals);
  const rounded = Math.round(numericAmount * factor) / factor;

  // Format with commas and fixed decimals
  const formatted = rounded.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return `THB ${formatted}`;
};

export const normalizeCrewLabel = s =>
  String(s)
    .trim()
    .replace(/[\u2013\u2014]/g, '-') // en/em dash -> hyphen
    .replace(/\s+/g, ' '); // collapse inner spaces (optional)

// Only remap these specific keys AND replace has_all → has_any
export const remapKeys = params => {
  const targetKeys = ['pub_amenities', 'pub_theme', 'pub_power_supply'];

  return Object.entries(params).reduce((acc, [key, value]) => {
    if (targetKeys.includes(key)) {
      const newKey = `pub_${key}`;
      let newValue = value;

      // Handle both string and array cases
      if (typeof value === 'string') {
        newValue = value.replace(/has_all:/g, 'has_any:');
      } else if (Array.isArray(value)) {
        newValue = value.map(v => (typeof v === 'string' ? v.replace(/has_all:/g, 'has_any:') : v));
      }

      acc[newKey] = newValue;
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});
};

export function cleanupOmiseArtifacts() {
  // 1) Try to gracefully close any open Omise modal
  try {
    if (
      typeof window !== 'undefined' &&
      window.OmiseCard &&
      typeof window.OmiseCard.close === 'function'
    ) {
      window.OmiseCard.close();
    }
  } catch (_) {}

  // 2) HIDE overlays & containers instead of removing them
  const hide = el => {
    if (!el) return;
    try {
      el.style.display = 'none';
      el.style.visibility = 'hidden';
      el.style.pointerEvents = 'none';
      el.setAttribute('aria-hidden', 'true');
    } catch (_) {}
  };

  try {
    document
      .querySelectorAll(
        '#OmiseCard-overlay, #OmiseCard-container, .omise-card-modal, .omise-card-overlay'
      )
      .forEach(hide);
  } catch (_) {}

  // 3) DO NOT REMOVE IFRAMES (this is what triggers the null postMessage crash)
  // If you really need to, only hide them:
  try {
    document
      .querySelectorAll('iframe[src*="omise.co"], iframe[id*="Omise"], iframe[name*="omise"]')
      .forEach(hide);
  } catch (_) {}

  // 4) DO NOT REMOVE THE SCRIPT TAG OR DELETE window.Omise / window.OmiseCard
}

export const isBefore4Days = (booking, days = 4) => {
  const start = booking?.attributes?.start;
  if (!start) return false;

  const startMs = new Date(start).getTime();
  const nowMs = Date.now();
  const diffHours = (startMs - nowMs) / (1000 * 60 * 60);
  return diffHours >= days * 24;
};

export const formatNumber = number =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    number
  );

export const getCancelEmailObject = (transaction, refundObj) => {
  const bookingStart = moment(transaction?.booking?.attributes?.start);
  const bookingEnd = moment(transaction?.booking?.attributes?.end);
  const currency = transaction?.attributes?.payoutTotal?.currency || 'THB';
  const refundPercent = refundObj?.refundPercent;
  const bookingDate = bookingStart.format('D MMM');
  const startTime = bookingStart.format('h:mm a');
  const endTime = bookingEnd.format('h:mm a');

  const lineItems = transaction?.attributes?.lineItems || [];
  const totalLineItem = getBookingBaseLineItem(lineItems);
  const refundCustomerCommission = lineItems.find(
    item => item.code === LINE_ITEM_CUSTOMER_COMMISSION
  );
  const refundProviderCommission = lineItems.find(
    item => item.code === LINE_ITEM_PROVIDER_COMMISSION
  );
  const refundCustomerOmiseFee = lineItems.find(item => item.code === LINE_ITEM_OMISE_FEE);
  const providerCommissionSubunits = getMoneySubunits(refundProviderCommission?.lineTotal);
  const calculatedProviderCommission =
    refundPercent == 50 ? providerCommissionSubunits / 2 : providerCommissionSubunits;
  const customerOmiseFee = Math.abs(getMoneySubunits(refundCustomerOmiseFee?.lineTotal) / 100);
  const customerCommissionAmount = Math.abs(
    getMoneySubunits(refundCustomerCommission?.lineTotal) / 100
  );
  const fallbackBaseSubunits = Math.max(
    0,
    getMoneySubunits(transaction?.attributes?.payinTotal) -
      Math.abs(getMoneySubunits(refundCustomerCommission?.lineTotal)) -
      Math.abs(getMoneySubunits(refundCustomerOmiseFee?.lineTotal))
  );
  const totalPriceSubunits =
    Math.abs(getMoneySubunits(totalLineItem?.lineTotal)) || fallbackBaseSubunits;
  const unitPriceSubunits =
    Math.abs(getMoneySubunits(totalLineItem?.unitPrice)) || totalPriceSubunits;
  const totalPrice = totalPriceSubunits / 100;
  const unitPrice = unitPriceSubunits / 100;
  const providerCommissionAmount = Math.abs(calculatedProviderCommission / 100);
  const quantity = Math.abs(
    Number(
      totalLineItem?.quantity?.toString?.() ||
        totalLineItem?.units?.toString?.() ||
        totalLineItem?.quantity ||
        totalLineItem?.units ||
        1
    )
  );
  const unitItem = `${currency} ${formatNumber(unitPrice)} x ${quantity}`;
  const totalUnitPrice = `${currency} ${formatNumber(totalPrice)}`;
  const validCurrency = refundPercent == 0 ? currency : `-${currency}`;

  const providerRefundAmount =
    refundPercent == 0 ? totalPrice : refundPercent == 50 ? totalPrice / 2 : 0;
  const customerRefundAmount =
    refundPercent == 0 ? 0 : refundPercent == 50 ? totalPrice / 2 : totalPrice;
  const providerRefundAmountWithCommission =
    providerRefundAmount > 0 && providerCommissionAmount > 0
      ? providerRefundAmount - providerCommissionAmount
      : 0;

  const refundAmount = `${validCurrency} ${formatNumber(customerRefundAmount)}`;
  const totalWithoutCommission = `${currency} ${formatNumber(providerRefundAmountWithCommission)}`;
  const providerAmount = `${validCurrency} ${formatNumber(providerRefundAmount)}`;
  const paymentFee = `${currency} ${formatNumber(customerOmiseFee)}`;
  const serviceFee = `${currency} ${formatNumber(customerCommissionAmount)}`;
  const formatedProviderRefundAmount = `${validCurrency} ${formatNumber(providerRefundAmount)}`;
  const formatedProviderCommissionAmount = `${currency} ${formatNumber(providerCommissionAmount)}`;

  const customerTotalPrice =
    refundPercent == 100
      ? customerOmiseFee + customerCommissionAmount
      : totalPrice - customerRefundAmount;
  const formatedCustomerTotalPrice = `${currency} ${formatNumber(customerTotalPrice)}`;

  const cancelObj = {
    unitItem,
    ...(customerRefundAmount > 0 ? { refundAmount } : {}),
    totalUnitPrice,
    paymentFee,
    serviceFee,
    providerAmount,
    totalWithoutCommission,
    formatedProviderRefundAmount: refundPercent == 50 ? formatedProviderRefundAmount : refundAmount,
    formatedProviderCommissionAmount,
    customerTotalPrice: formatedCustomerTotalPrice,
    isHalfRefund: refundAmount === 50 ? true : false,
  };
  return cancelObj;
};

export function loadOmiseScript() {
  if (typeof window !== 'undefined' && window.OmiseCard) {
    return Promise.resolve('ready');
  }
  if (omiseLoaderPromise) return omiseLoaderPromise;

  omiseLoaderPromise = new Promise((resolve, reject) => {
    let script = document.querySelector(`script[${OMISE_SCRIPT_ATTR}="true"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = OMISE_SRC;
      script.async = true;
      script.setAttribute(OMISE_SCRIPT_ATTR, 'true');
      document.body.appendChild(script);
    }

    const onLoad = () => resolve('loaded');
    const onError = () => reject(new Error('Failed to load Omise script'));

    // If script already complete (from cache)
    if (script.readyState === 'complete' || script.readyState === 'loaded') {
      resolve('loaded');
    } else {
      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });
    }

    // Safety timeout in case load event never fires
    setTimeout(() => {
      if (window.OmiseCard) resolve('ready');
    }, 8000);
  });

  return omiseLoaderPromise;
}

export const getProcessName = pageData => {
  const { transaction, listing } = pageData || {};
  const processName = transaction?.id
    ? transaction?.attributes?.processName
    : listing?.id
    ? listing?.attributes?.publicData?.transactionProcessAlias?.split('/')[0]
    : null;
  return resolveLatestProcessName(processName);
};

const hasFilteredTransition = (transitions = []) =>
  (transitions || []).some(t => FILTERED_TRANSITIONS_SET.has(t?.transition));

export const splitAcceptedTransactions = (transactions = []) => {
  const result = {
    acceptedCard: [],
    acceptedNonCard: [],
    allAccepted: [],
    notAccepted: [],
  };

  if (!Array.isArray(transactions) || transactions.length === 0) return result;

  transactions.forEach(tx => {
    const transitions = tx?.attributes?.transitions ?? [];
    const paymentMethod = tx?.attributes?.protectedData?.paymentMethod;

    // 🚫 HARD FILTER — skip completely
    if (hasFilteredTransition(transitions)) {
      return;
    }

    const hasConfirm = transitions.some(
      t =>
        t?.transition === TRANSITIONS.CONFIRM_PAYMENT ||
        t?.transition === TRANSITIONS.OPERATOR_CONFIRM_PAYMENT ||
        t?.transition === TRANSITIONS.CONFIRM_PAY_LATER_PAYMENT ||
        t?.transition === TRANSITIONS.OPERATOR_CONFIRM_PAY_LATER_PAYMENT
    );

    const hasAccept = transitions.some(
      t =>
        t?.transition === TRANSITIONS.ACCEPT ||
        t?.transition === TRANSITIONS.OPERATOR_ACCEPT ||
        t?.transition === TRANSITIONS.ACCEPT_PAY_LATER ||
        t?.transition === TRANSITIONS.OPERATOR_ACCEPT_PAY_LATER
    );

    const isAccepted = hasAccept;

    if (isAccepted) {
      if (paymentMethod === PAYMENT_METHODS.CARD) {
        result.acceptedCard.push(tx);
      } else {
        result.acceptedNonCard.push(tx);
      }
      result.allAccepted.push(tx);
    } else {
      result.notAccepted.push(tx);
    }
  });

  return result;
};

// Flat list of accepted transactions
export const filterAcceptedTransactions = (transactions = []) => {
  const { allAccepted } = splitAcceptedTransactions(transactions);
  return allAccepted;
};

export const filterConfirmedTransactions = transactions => {
  if (!Array.isArray(transactions)) return [];

  return transactions.filter(tx => {
    const transitionsArr = tx?.attributes?.transitions || [];

    // Card and pay-later transactions use different confirmation transitions.
    // Both represent a confirmed payment and can contribute to provider earnings.
    const hasConfirmPayment = transitionsArr.some(t =>
      [
        TRANSITIONS.CONFIRM_PAYMENT,
        TRANSITIONS.OPERATOR_CONFIRM_PAYMENT,
        TRANSITIONS.CONFIRM_PAY_LATER_PAYMENT,
        TRANSITIONS.OPERATOR_CONFIRM_PAY_LATER_PAYMENT,
      ].includes(t?.transition)
    );

    return hasConfirmPayment;
  });
};

export const getProfileImageUrl = user => {
  const profile = user?.attributes?.profile || {};
  return (
    profile?.publicData?.avatarUrl ||
    profile?.image ||
    'https://dummyimage.com/128x128/ddd/000.png&text=User'
  );
};

export const getUserDisplayName = user => {
  if (!user.id) return null;
  return user?.attributes?.profile?.displayName;
};

export const getIconNameForState = nextState => NEXT_STATE_ICON_MAP[nextState] || null;

export const getImageVariants = () => [
  // Scaled variants for large images
  'variants.scaled-small',
  'variants.scaled-medium',
  'variants.scaled-large',
  'variants.scaled-xlarge',
  'variants.scaled-xlarge',
  'variants.scaled-xlarge-2x',
  'variants.scaled-xlarge-6x',

  // Listing page
  'variants.landscape-crop',
  'variants.landscape-crop2x',
  'variants.landscape-crop4x',
  'variants.landscape-crop6x',

  // Cropped variants for listing thumbnail images
  `variants.listing-card`,
  `variants.listing-card-2x`,
  `variants.listing-card-4x`,
  `variants.listing-card-6x`,

  // Social media
  'variants.facebook',
  'variants.twitter',

  // Avatars
  'variants.square-small',
  'variants.square-small2x',
];

export const getFileExtension = attachment => {
  if (!attachment) return null;
  const name = attachment.title || attachment.fileName || attachment.url || '';
  const parts = name.split('?')[0].split('.');
  if (parts.length < 2) return null;
  return parts.pop().toLowerCase();
};

export const getFileTypeIcon = ext => {
  switch (ext) {
    case 'pdf':
      // Red PDF icon
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="2" width="18" height="20" rx="3" fill="#EF4444" />
          <path d="M8 9h4a2 2 0 0 1 0 4H9.5V15H8V9zm1.5 3H12a.5.5 0 0 0 0-1H9.5v1z" fill="white" />
          <text
            x="12"
            y="19"
            textAnchor="middle"
            fontSize="7"
            fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
            fill="white"
          >
            PDF
          </text>
        </svg>
      );

    case 'doc':
    case 'docx':
      // Blue Word icon
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="2" width="18" height="20" rx="3" fill="#2563EB" />
          <path d="M8 8h2l1 5 1-5h2l1 5 1-5h2l-2 8h-2l-1-4-1 4h-2L8 8z" fill="white" />
          <text
            x="12"
            y="19"
            textAnchor="middle"
            fontSize="6"
            fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
            fill="white"
          >
            DOC
          </text>
        </svg>
      );

    case 'txt':
      // Neutral text icon
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="2" width="18" height="20" rx="3" fill="#4B5563" />
          <line x1="7" y1="9" x2="17" y2="9" stroke="white" strokeWidth="1.5" />
          <line x1="7" y1="12" x2="17" y2="12" stroke="white" strokeWidth="1.5" />
          <line x1="7" y1="15" x2="13" y2="15" stroke="white" strokeWidth="1.5" />
          <text
            x="12"
            y="19"
            textAnchor="middle"
            fontSize="6"
            fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
            fill="white"
          >
            TXT
          </text>
        </svg>
      );

    default:
      // Generic file icon
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 2h7l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" fill="#9CA3AF" />
          <path d="M13 2v5h5" fill="#E5E7EB" />
          <line x1="8" y1="12" x2="16" y2="12" stroke="#374151" strokeWidth="1.4" />
          <line x1="8" y1="15" x2="14" y2="15" stroke="#374151" strokeWidth="1.4" />
        </svg>
      );
  }
};

export function getOvertimePeriod(orderData, selectedBookingModal) {
  const overTimeHours = parseInt(selectedBookingModal?.overTime, 10);
  const fullDayEnd = selectedBookingModal?.selectedTimeSlots?.fullDayTimes?.end;

  if (overTimeHours > 0 && fullDayEnd) {
    // Get the booking date
    const bookingDate = moment(orderData?.bookingStart).format('YYYY-MM-DD');

    // Combine booking date with fullDayTimes.end
    const overtimeStart = moment(`${bookingDate} ${fullDayEnd}`, 'YYYY-MM-DD HH:mm');

    const overtimeEnd = overtimeStart.clone().add(overTimeHours, 'hours');

    const startFormatted = overtimeStart.format('h:mm a');
    const endFormatted = overtimeEnd.format('h:mm a');

    return ` ${startFormatted} -- ${endFormatted}`;
  }

  return null;
}

export function calculateRateWithOvertime(selectedBookingModal, overtimePercentage) {
  // Extract necessary values from orderData
  const { rate, overTime } = selectedBookingModal || {};

  // Convert rate to a number (since it's a string)
  const numericRate = parseFloat(rate);
  // Calculate the percentage of the rate
  const percentageRate = (numericRate * overtimePercentage) / 100;
  // Calculate the total overtime rate (percentageRate * overTime)
  const totalRate = percentageRate * parseInt(overTime, 10);
  return totalRate;
}

const CANCEL_SET = new Set([
  TRANSITIONS.CANCEL,
  TRANSITIONS.CANCEL_BY_CUSTOMER,
  TRANSITIONS.CANCEL_BY_PROVIDER,
  TRANSITIONS.CANCEL_BOOKING_REQUEST_PAYMENT,
  TRANSITIONS.CANCEL_BOOKING_REQUEST,
  TRANSITIONS.CANCEL_PAY_LATER_BOOKING_REQUEST,
  TRANSITIONS.CANCEL_FROM_DISPUTED,
  TRANSITIONS.AUTO_CANCEL_FROM_DISPUTED,
  TRANSITIONS.COMPLETE_AFTER_CANCEL,
  TRANSITIONS.OPERATOR_COMPLETE_AFTER_CANCEL,
  TRANSITIONS.INITIATE_REFUND, // optional: if you consider refund as part of cancellation flow
]);

const DASHBOARD_COMPLETED = new Set([
  TRANSITIONS.MARK_RECEIVED_FROM_DISPUTED,
  TRANSITIONS.PAYOUT_COMPLETE,
  TRANSITIONS.OPERATOR_PAYOUT_COMPLETE,
  TRANSITIONS.REVIEW_1_BY_PROVIDER,
  TRANSITIONS.REVIEW_2_BY_PROVIDER,
  TRANSITIONS.REVIEW_1_BY_CUSTOMER,
  TRANSITIONS.REVIEW_2_BY_CUSTOMER,
]);

export const isCanceledTransaction = (transitionsArr = []) => {
  return transitionsArr.some(t => CANCEL_SET.has(t.transition));
};

export const isPaidOutTransaction = (transitionsArr = []) => {
  return transitionsArr.some(t => DASHBOARD_COMPLETED.has(t.transition));
};

export // OmisePaymentForm.js (top of file)

const validateCardDetails = (cardDetails, intl) => {
  const errors = {};

  const rawName = (cardDetails?.cardName || '').trim();
  const rawNumber = (cardDetails?.cardNumber || '').replace(/\s+/g, '');
  const rawMonth = (cardDetails?.cardExpMonth || '').trim();
  const rawYear = (cardDetails?.cardExpYear || '').trim();
  const rawCvv = (cardDetails?.securityCode || '').trim();

  // Name on card
  if (!rawName) {
    errors.cardName = 'Name on card is required.';
  }

  // Card number: only digits, exactly 16, and Luhn check
  if (!rawNumber) {
    errors.cardNumber = 'Card number is required.';
  } else if (detectPaymentCard(rawNumber).unsupported) {
    errors.cardNumber = 'We currently accept Visa, Mastercard, and JCB';
  } else if (!/^\d+$/.test(rawNumber)) {
    errors.cardNumber = 'Card number must contain digits only.';
  } else if (rawNumber.length !== 16) {
    errors.cardNumber = 'Card number must be 16 digits.';
  } else if (!luhnCheck(rawNumber)) {
    errors.cardNumber = 'Card number is invalid.';
  }

  // Expiry month
  const month = parseInt(rawMonth, 10);
  if (!rawMonth) {
    errors.cardExpMonth = 'Expiry month is required.';
  } else if (!/^\d+$/.test(rawMonth)) {
    errors.cardExpMonth = 'Expiry month must be numeric.';
  } else if (month < 1 || month > 12) {
    errors.cardExpMonth = 'Expiry month must be between 1 and 12.';
  }

  // Expiry year (YYYY)
  const year = parseInt(rawYear, 10);
  const now = new Date();
  const currentYear = now.getFullYear();
  const maxYear = currentYear + 20; // reasonable upper bound

  if (!rawYear) {
    errors.cardExpYear = 'Expiry year is required.';
  } else if (!/^\d{4}$/.test(rawYear)) {
    errors.cardExpYear = 'Expiry year must be 4 digits.';
  } else if (year < currentYear) {
    errors.cardExpYear = 'Card has expired.';
  } else if (year > maxYear) {
    errors.cardExpYear = 'Expiry year looks invalid.';
  }

  // Combined expiry check: not in the past (month/year together)
  if (!errors.cardExpMonth && !errors.cardExpYear) {
    const currentMonth = now.getMonth() + 1;
    if (year === currentYear && month < currentMonth) {
      errors.cardExpMonth = 'Card has expired.';
    }
  }

  // CVV: exactly 3 digits
  if (!rawCvv) {
    errors.securityCode = 'CVC is required.';
  } else if (!/^\d+$/.test(rawCvv)) {
    errors.securityCode = 'CVC must be numeric.';
  } else if (rawCvv.length !== 3) {
    errors.securityCode = 'CVC must be 3 digits.';
  }

  const isValid = Object.keys(errors).length === 0;
  return { isValid, errors };
};

// Simple Luhn algorithm for card number
const luhnCheck = numStr => {
  let sum = 0;
  let shouldDouble = false;

  for (let i = numStr.length - 1; i >= 0; i--) {
    let digit = parseInt(numStr[i], 10);

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};

export const createVaultToken = async ({
  name,
  number,
  expMonth,
  expYear,
  securityCode,
  omisePublicKey,
  authorId,
  intl,
}) => {
  const GENERIC_MSG = intl.formatMessage({ id: 'Payment.Error.genericProcessingFailure' });

  if (!omisePublicKey) {
    const err = new Error(GENERIC_MSG);
    err.source = 'vault';
    throw err;
  }

  const safeName = (name || '').trim();
  if (!safeName) {
    // keep this one as a field error – it’s a user input problem
    const err = new Error("Name can't be blank");
    err.fields = { cardName: "Name can't be blank" };
    err.source = 'vault';
    throw err;
  }

  // 🔐 subMerchantId is mandatory – if it fails or is empty, stop here
  // let subMerchantId = null;
  // try {
  //   subMerchantId = await getSubMerchantIdApi({ authorId });
  // } catch (e) {
  //   console.error('getSubMerchantIdApi failed:', e);
  //   const err = new Error(GENERIC_MSG);
  //   err.source = 'subMerchant';
  //   throw err;
  // }

  // if (!subMerchantId) {
  //   const err = new Error(GENERIC_MSG);
  //   err.source = 'subMerchant';
  //   throw err;
  // }

  const params = new URLSearchParams();
  params.append('card[name]', safeName);
  params.append('card[number]', (number || '').replace(/\s/g, ''));
  params.append('card[expiration_month]', expMonth || '');
  params.append('card[expiration_year]', expYear || '');
  params.append('card[security_code]', securityCode || '');

  const headers = {
    'Content-Type': 'application/x-www-form-urlencoded',
    Authorization: `Basic ${window.btoa(`${omisePublicKey}:`)}`,
    // SUB_MERCHANT_ID: subMerchantId,
  };

  let data = {};
  let res;
  try {
    res = await fetch('https://vault.omise.co/tokens', {
      method: 'POST',
      headers,
      body: params.toString(),
    });
  } catch (e) {
    console.error('Network error calling Omise vault:', e);
    const err = new Error(GENERIC_MSG);
    err.source = 'vault';
    throw err;
  }

  // swallow any bad JSON and turn into a generic UI error
  try {
    data = await res.json();
  } catch (e) {
    console.error('Omise vault JSON parse error:', e);
    const err = new Error(GENERIC_MSG);
    err.source = 'vault';
    throw err;
  }

  if (!res.ok) {
    // Omise vault usually returns { message, code, ... }
    const msgFromOmise = data?.message;
    const err = new Error(msgFromOmise || GENERIC_MSG);
    err.raw = data;
    err.source = 'vault';

    // still map field-level errors if they exist
    const lower = String(msgFromOmise || '').toLowerCase();
    const fields = {};
    if (lower.includes('name') && lower.includes("can't be blank")) fields.cardName = msgFromOmise;
    if (lower.includes('number')) fields.cardNumber = msgFromOmise;
    if (lower.includes('expiration')) fields.cardExpiry = msgFromOmise;
    if (lower.includes('security code') || lower.includes('cvv') || lower.includes('cvc')) {
      fields.securityCode = msgFromOmise;
    }
    if (Object.keys(fields).length) err.fields = fields;

    throw err;
  }

  return data;
};

const NUMERIC_FIELD_KEYS = new Set([
  'property_size_m2',
  'ceiling_height',
  'number_bedrooms',
  'number_bathrooms',
  'main_floor_number',
]);

export const formatMaybeNumber = (intl, key, value) => {
  if (!NUMERIC_FIELD_KEYS.has(key)) return value;

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return value;

  return intl.formatNumber(numericValue, {
    maximumFractionDigits: 0,
  });
};

export const calculateQuantityFromHours = (startDate, endDate) => {
  // Note: the last parameter (true) ensures that floats are returned.
  return moment(endDate).diff(moment(startDate), 'hours', true);
};
export const detectBrowserLocale = () => {
  // 1) Detect using browser language
  let browserLang = 'en';
  if (typeof navigator !== 'undefined') {
    browserLang = (navigator.language || navigator.userLanguage || 'en').toLowerCase();
  }
  if (browserLang.startsWith('th')) return 'th';

  // 2) Fallback using timezone
  let tz = '';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (e) {}

  if (tz === 'Asia/Bangkok') return 'th';

  return 'en';
};

// Normalize locale to supported app locales
export const normalizeLocale = raw => {
  const v = String(raw || '').toLowerCase();
  return v === 'th' || v.startsWith('th') ? 'th' : 'en';
};

export const LOCALE_STORAGE_KEY = 'locale';
export const LOCALE_CHANGE_EVENT = 'sceneaccess:locale-change';

export const getLocaleFromSearchParams = search => {
  if (!search) return null;
  try {
    const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
    const queryLocale = params.get('locale') || params.get('lang');
    return queryLocale ? normalizeLocale(queryLocale) : null;
  } catch (e) {
    return null;
  }
};

export const getLocaleFromPathname = pathname => {
  if (!pathname) return null;
  const match = String(pathname)
    .toLowerCase()
    .match(/^\/(th(?:-[a-z]+)?)(\/|$)/);
  return match ? normalizeLocale(match[1]) : null;
};

export const getLocaleFromWindowLocation = () => {
  if (typeof window === 'undefined') return null;
  const { search, pathname } = window.location || {};
  return getLocaleFromSearchParams(search) || getLocaleFromPathname(pathname);
};

// ✅ This one is now just detection (no storage)
export const getDefaultLocale = () => {
  return detectBrowserLocale();
};

// ✅ read saved locale from currentUser
export const getLocaleFromCurrentUser = user => {
  return user?.attributes?.profile?.privateData?.selectedLanguage || null;
};

export const isPriceOnlyUpdate = updateValues => {
  const hasTitle = typeof updateValues?.title !== 'undefined';
  const pd = updateValues?.publicData || {};
  const keys = Object.keys(pd);

  if (hasTitle) return false;
  if (!keys.length) return false;

  const PRICE_KEYS = new Set([
    'price',
    'pricingModel',
    'autoPricing',
    'baseRates',
    'pricingMatrices',
    'addons',
    'overtimePercentage',
    'minimumHours',
    'halfDayTimes',
    'fullDayTimes',
    'enabledRates',
  ]);

  return keys.every(k => PRICE_KEYS.has(k));
};

export const localizeListingFieldConfigs = (listingFieldConfigs = [], locale = 'en') => {
  if (locale !== 'th') return listingFieldConfigs;

  return listingFieldConfigs.map(f => {
    const heading = FIELD_HEADING_TH[f.key] || f.heading || f.key;

    // enumOptions is what Sharetribe config uses for "Select one / Select multiple"
    const enumOptions = Array.isArray(f.enumOptions)
      ? f.enumOptions.map(opt => ({
          ...opt,
          label: OPTION_LABEL_TH[opt.option] || opt.label,
        }))
      : f.enumOptions;

    return {
      ...f,
      heading,
      label: heading, // in case you use label instead of heading in some places
      enumOptions,
    };
  });
};

/**
 * Get locale from localStorage (for non-logged-in users)
 * Returns null if not found or if running on server
 */
export const getLocaleFromLocalStorage = () => {
  if (typeof window !== 'undefined') {
    try {
      return window.localStorage.getItem(LOCALE_STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }
  return null;
};

/**
 * Persist a locale selection and notify listeners in the current browser tab.
 *
 * The native "storage" event only fires in other tabs, so the custom event is
 * needed for the active app to update without a page reload.
 */
export const persistLocale = rawLocale => {
  const locale = normalizeLocale(rawLocale);

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch (e) {
      // The current UI can still update when storage is unavailable.
    }

    try {
      window.dispatchEvent(
        new window.CustomEvent(LOCALE_CHANGE_EVENT, {
          detail: { locale },
        })
      );
    } catch (e) {
      // Ignore event API failures in legacy/non-browser environments.
    }
  }

  return locale;
};

/**
 * Resolve locale from multiple sources (single source of truth):
 * URL > localStorage > user profile > config > browser detection
 */
export const resolveLocale = ({ currentUser, config, locationSearch, locationPathname } = {}) => {
  const urlLocale =
    getLocaleFromSearchParams(locationSearch) ||
    getLocaleFromPathname(locationPathname) ||
    getLocaleFromWindowLocation();
  const storedLocale = getLocaleFromLocalStorage();
  const userLocale = getLocaleFromCurrentUser(currentUser);
  const configLocale = config?.localization?.locale;
  const detectedLocale = detectBrowserLocale();

  return normalizeLocale(
    urlLocale || storedLocale || userLocale || configLocale || detectedLocale || 'en'
  );
};

export const appendLocaleParam = (url, locale) => {
  if (!url) return url;
  const normalizedLocale = normalizeLocale(locale);
  if (normalizedLocale !== 'th') return url;

  const [base, hash] = String(url).split('#');
  const [path, query] = base.split('?');
  const params = new URLSearchParams(query || '');
  if (params.get('locale') || params.get('lang')) {
    return url;
  }
  params.set('locale', normalizedLocale);
  const nextBase = `${path}?${params.toString()}`;
  return hash ? `${nextBase}#${hash}` : nextBase;
};

/**
 * Get effective locale - works for both logged-in and non-logged-in users
 * Priority:
 * - Logged-in users: user profile > localStorage > 'en'
 * - Non-logged-in users: localStorage > 'en'
 */
export const getEffectiveLocale = currentUser => {
  // First check if user is logged in and has a saved locale
  if (currentUser?.id) {
    const userLocale = getLocaleFromCurrentUser(currentUser);
    if (userLocale) {
      return userLocale;
    }
  }

  // Fall back to localStorage (works for both logged-in and non-logged-in)
  const localStorageLocale = getLocaleFromLocalStorage();
  if (localStorageLocale) {
    return localStorageLocale;
  }

  // Default to 'en'
  return 'en';
};

export const getLocalizedListingData = (listing, locale = 'en') => {
  const attrs = listing?.attributes || {};
  const baseTitle = attrs.title || '';
  const baseDescription = attrs.description || '';
  const basePublicData = attrs.publicData || {};
  // Your thLanguage lives under publicData.thLanguage
  const thLanguage = basePublicData?.thLanguage || {};
  const lang = thLanguage?.[locale] || {};

  const localizedTitle = lang?.title || baseTitle;
  const localizedDescription = lang?.description || baseDescription;
  const hasBasePropertySize = Object.prototype.hasOwnProperty.call(
    basePublicData,
    'property_size_m2'
  );
  const hasLocalizedPropertySize = Object.prototype.hasOwnProperty.call(
    lang?.publicData || {},
    'property_size_m2'
  );
  const propertySizeMaybe =
    hasBasePropertySize || hasLocalizedPropertySize
      ? {
          property_size_m2: hasBasePropertySize
            ? basePublicData.property_size_m2
            : lang.publicData.property_size_m2,
        }
      : {};

  // Override ONLY the fields you translated
  const localizedPublicData = {
    ...basePublicData,
    ...(lang?.publicData || {}),
    ...propertySizeMaybe,
  };

  return {
    title: localizedTitle,
    description: localizedDescription,
    publicData: localizedPublicData,
  };
};

export const isThaiLocale = (intl, locale) => {
  // If locale is explicitly provided, use it
  if (locale) {
    const loc = String(locale).toLowerCase();
    return loc === 'th' || loc.startsWith('th-');
  }

  // Check intl locale
  if (intl?.locale) {
    const loc = String(intl.locale).toLowerCase();
    return loc === 'th' || loc.startsWith('th-');
  }

  // Fall back to localStorage for non-logged-in users
  const localStorageLocale = getLocaleFromLocalStorage();
  if (localStorageLocale) {
    const loc = String(localStorageLocale).toLowerCase();
    return loc === 'th' || loc.startsWith('th-');
  }

  return false;
};

export const getBookingStatusIntlKey = status =>
  transactionStatusIntlMap[String(status || '').toLowerCase()] || 'BookingStatus.pending';

const VAT = 0.07;
const THB_IN_SATANG = 100;

// ---- TEST ACCOUNT MDR (from public docs) ----
const TEST_MDR = {
  CARD_LOCAL: 0.0365, // 3.65%
  CARD_INTL: 0.0365, // same in test/docs
  PROMPTPAY: 0.0165, // 1.65%
  FLAT_THB: 10, // 10 THB flat (mobile + direct debit)
};

// ---- LIVE ACCOUNT MDR (from your screenshots) ----
const LIVE_MDR = {
  CARD_LOCAL: 0.0325, // 3.25%  (TH card)
  CARD_INTL: 0.033, // 3.3%   (non-TH card)
  PROMPTPAY: 0.015, // 1.5%
  FLAT_THB: 10, // 10 THB flat
};

const getMdrConfig = mode => (mode === 'production' ? LIVE_MDR : TEST_MDR);

const omiseFeeForTotal = (totalMinor, rate) => {
  const core = Math.round(totalMinor * rate);
  const vat = Math.round(core * VAT);
  return core + vat;
};

const computePercentageFeeGrossedUp = (baseAmount, rate) => {
  if (!baseAmount) return 0;

  let core = Math.round(baseAmount * rate);
  let vat = Math.round(core * VAT);
  let fee = core + vat;

  for (let i = 0; i < 10; i += 1) {
    const total = baseAmount + fee;
    const newFee = omiseFeeForTotal(total, rate);
    if (newFee === fee) break;
    fee = newFee;
  }

  return fee;
};

export const calculateOmiseFee = ({
  baseAmount,
  paymentMethod,
  countryCode,
  mode = process.env.REACT_APP_ENV,
}) => {
  if (!baseAmount) return 0;

  const MDR = getMdrConfig(mode);
  let fee = 0;
  if (paymentMethod === PAYMENT_METHODS.CARD) {
    const isThaiCard = countryCode === 'TH';
    const rate = isThaiCard ? MDR.CARD_LOCAL : MDR.CARD_INTL;
    fee = computePercentageFeeGrossedUp(baseAmount, rate);
  } else if (paymentMethod === PAYMENT_METHODS.PROMPTPAY) {
    fee = computePercentageFeeGrossedUp(baseAmount, MDR.PROMPTPAY);
  } else if (paymentMethod === PAYMENT_METHODS.MOBILE_BANKING) {
    const coreFlat = MDR.FLAT_THB * THB_IN_SATANG;
    const vatFlat = Math.round(coreFlat * VAT);
    fee = coreFlat + vatFlat;
  } else {
    fee = 0;
  }

  return fee;
};

export const capitalizeFirstChar = value => {
  if (!value || typeof value !== 'string') return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const maskBankAccountNumber = (accountNumber, visibleDigits = 4) => {
  if (!accountNumber) return '';

  const str = String(accountNumber);
  const lastDigits = str.slice(-visibleDigits);
  const maskedLength = Math.max(str.length - visibleDigits, 0);

  return `${'•'.repeat(maskedLength)}${lastDigits}`;
};

export const getListingAddress = (listing, thLanguage) => {
  // 1️⃣ Default structured location
  const defaultLocation = listing?.attributes?.publicData?.location;
  const defaultLocationAddress = defaultLocation?.address;

  if (typeof defaultLocationAddress === 'string' && defaultLocationAddress.trim()) {
    return {
      address: defaultLocationAddress,
      location: defaultLocation,
      source: 'default.location',
    };
  }

  // 2️⃣ Thai language structured location
  const thLocation = thLanguage?.th?.publicData?.location;
  const thLocationAddress = thLocation?.address;

  if (typeof thLocationAddress === 'string' && thLocationAddress.trim()) {
    return {
      address: thLocationAddress,
      location: thLocation,
      source: 'th.location',
    };
  }

  // 3️⃣ Default detailed address
  const defaultDetailedAddress = listing?.attributes?.publicData?.detailedAddress;

  if (typeof defaultDetailedAddress === 'string' && defaultDetailedAddress.trim()) {
    return {
      address: defaultDetailedAddress,
      location: null,
      source: 'default.detailedAddress',
    };
  }

  // 4️⃣ Thai language detailed address
  const thDetailedAddress = thLanguage?.th?.publicData?.detailedAddress;

  if (typeof thDetailedAddress === 'string' && thDetailedAddress.trim()) {
    return {
      address: thDetailedAddress,
      location: null,
      source: 'th.detailedAddress',
    };
  }

  return {
    address: null,
    location: null,
    source: null,
  };
};

export const getSubMerchantIdFromUser = currentUser => {
  return currentUser?.attributes?.profile?.privateData?.subMerchantId;
};

export const uuidStr = v => {
  if (!v) return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'object') return v?.imageId?.uuid || v.uuid || v?.id?.uuid || null;
  return null;
};

export const filterEmptyBlocksSections = pageData => {
  if (!pageData || !Array.isArray(pageData.sections)) {
    return pageData;
  }

  return {
    ...pageData,
    sections: pageData.sections.filter(section => {
      // If blocks is undefined, keep the section (hero, simple sections, etc.)
      if (!('blocks' in section)) {
        return true;
      }

      // Remove if blocks is null or empty array
      if (!section.blocks || section.blocks.length === 0) {
        return false;
      }

      return true;
    }),
  };
};

export const uploadQueue = async (files, handler, concurrency = 4) => {
  const queue = [...files];
  const workers = Array.from({ length: concurrency }).map(async () => {
    while (queue.length) {
      const file = queue.shift();
      await handler(file);
    }
  });
  await Promise.all(workers);
};

export const saveDepositAgreement = value => {
  localStorage.setItem(DEPOSIT_CHECK_KEY, JSON.stringify(value));
};

export const getDepositAgreement = () => {
  return JSON.parse(localStorage.getItem(DEPOSIT_CHECK_KEY) || 'false');
};

export const saveDepositInstructionsState = value => {
  localStorage.setItem(DEPOSIT_INSTRUCTIONS_KEY, JSON.stringify(value));
};

export const getDepositInstructionsState = () => {
  return JSON.parse(localStorage.getItem(DEPOSIT_INSTRUCTIONS_KEY) || 'false');
};

export const detectCardBrand = cardNumber => {
  const { brand } = detectPaymentCard(cardNumber);
  return brand ? (brand === 'american-express' ? 'AMEX' : brand.toUpperCase()) : 'CARD';
};

export const generateFullDayTimes = () => {
  const times = [];

  for (let hour = 0; hour < 24; hour++) {
    const date = new Date();
    date.setHours(hour, 0, 0, 0);

    times.push({
      timeOfDay: moment(date).format('HH:mm'),
      timestamp: date.getTime(),
    });
  }

  return times;
};

export const createDateTimeFromDateAndTime = (selectedDate, selectedTime) => {
  if (!selectedDate || !selectedTime) {
    return null;
  }

  const [hours, minutes] = selectedTime.split(':').map(Number);

  const newDate = new Date(selectedDate);

  newDate.setHours(hours);
  newDate.setMinutes(minutes);
  newDate.setSeconds(0);
  newDate.setMilliseconds(0);

  return newDate.getTime();
};
